import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	CheckCircle2,
	FileDown,
	Loader2,
	Pencil,
	Play,
	Plus,
	Sparkles,
	Trash2,
	XCircle,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
	ConfirmDialog,
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { InfoDialog, InfoDialogSection } from "@/components/ui/info-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NoticeBanner } from "@/components/ui/notice-banner";
import { PageNote } from "@/components/ui/page-legend";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/features/auth/use-auth";
import { contractsApi } from "@/features/contracts/contracts-api";
import { ProducerSelector } from "@/features/contracts/producer-selector";
import { buildFm1CompareSheets, fm1CompareFileName } from "@/features/outbound/fm1-compare-export";
import { invisibleHint } from "@/features/outbound/invisible-diff";
import { InvisibleFlag } from "@/features/outbound/invisible-flag";
import {
	type AggregateDiffRow,
	type Fm1Comparison,
	type OutboundConnection,
	type OutboundSchemaColumn,
	type OutboundSchemaSnapshot,
	outboundApi,
	type SfyColumnMapping,
	type SfyConnectionContract,
	type SfyConnectionQuery,
	type SfyExplainReport,
	type SfyRowDifference,
	type SfySetDiff,
	type SfySnapshotMode,
	type SfyVerificationReport,
	type UpsertSfyQueryRequest,
} from "@/features/outbound/outbound-api";
import {
	buildAggregate,
	buildSourceSqlAndMappings,
	defaultCheckedColumns,
	deriveClientPrefix,
	getTemplate,
	OUR_TABLE_TEMPLATES,
	templateDrift,
} from "@/features/outbound/sfy-query-templates";
import { resolveSfyConnectionId, sfySearchSchema } from "@/features/outbound/sfy-search";
import { type ExcelColumn, exportToExcel } from "@/lib/export-excel";
import { ensureElementVisible } from "@/lib/route-guards";
import { cn } from "@/lib/utils";

const TEXTAREA_CLASS =
	"w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

export const Route = createFileRoute("/_auth/konfiguracja/sfy")({
	validateSearch: (search) => sfySearchSchema.parse(search),
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(context.queryClient, "sidebar.system.sfy", location.href);
	},
	component: SfyConfigPage,
});

// Inline notatnik per polaczenie (uwagi dla producenta). Renderowany z key={connectionId},
// wiec stan resetuje sie przy zmianie polaczenia. Zapis dedykowanym endpointem — nie rusza
// pozostalych pol polaczenia ani statusu testu.
function ConnectionNotes({
	connectionId,
	initialNotes,
}: {
	connectionId: number;
	initialNotes: string;
}) {
	const qc = useQueryClient();
	const [text, setText] = useState(initialNotes);
	const dirty = text !== initialNotes;
	const saveMutation = useMutation({
		mutationFn: () => outboundApi.updateConnectionNotes(connectionId, text.trim() || null),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["outbound", "connections", "sfy"] }),
	});
	return (
		<div className="mt-3 space-y-1.5">
			<div className="flex items-center justify-between max-md:flex-wrap max-md:gap-2">
				<Label className="text-xs text-muted-foreground">
					Uwagi dla producenta (notatnik — np. dodatkowe kolumny wyliczane)
				</Label>
				<Button
					size="sm"
					variant="outline"
					disabled={!dirty || saveMutation.isPending}
					onClick={() => saveMutation.mutate()}
					className="max-md:h-9"
				>
					{saveMutation.isPending ? (
						<Loader2 className="h-3 w-3 animate-spin" />
					) : dirty ? (
						"Zapisz uwagi"
					) : (
						"Zapisano"
					)}
				</Button>
			</div>
			<textarea
				className={`${TEXTAREA_CLASS} min-h-20 font-mono text-xs`}
				value={text}
				onChange={(e) => setText(e.target.value)}
				placeholder="np. w sprzedazy dodatkowa kolumna wyliczana: (CASE WHEN S.IDFIRM = 897304 THEN 1 ELSE 0 END) AS ecommerce"
			/>
		</div>
	);
}

function SfyConfigPage() {
	const { user } = useAuth();
	const isSuperAdmin = !!user?.roles?.includes("super_admin");
	// Wybrane polaczenie siedzi w URL (?polaczenie=), zeby powrot z przegladu i
	// odswiezenie strony pokazywaly ostatnio uzywanego producenta, a nie pierwszego.
	const { polaczenie } = Route.useSearch();
	const navigate = Route.useNavigate();
	const setSelectedConnectionId = (id: number | null) =>
		navigate({ search: (prev) => ({ ...prev, polaczenie: id ?? undefined }), replace: true });
	const [addConnectionOpen, setAddConnectionOpen] = useState(false);
	const [editConnectionOpen, setEditConnectionOpen] = useState(false);
	const [deleteConnectionOpen, setDeleteConnectionOpen] = useState(false);
	const qc = useQueryClient();

	const connectionsQuery = useQuery({
		queryKey: ["outbound", "connections", "sfy"],
		queryFn: () => outboundApi.listConnections(false),
		enabled: isSuperAdmin,
	});

	const selectedConnectionId = resolveSfyConnectionId(polaczenie, connectionsQuery.data ?? []);

	if (!isSuperAdmin) {
		return (
			<div className="p-6">
				<p className="text-muted-foreground">Brak dostepu. Wymagana rola super_admin.</p>
			</div>
		);
	}

	const selectedConnection = connectionsQuery.data?.find((c) => c.id === selectedConnectionId);

	return (
		<div className="p-6 space-y-6 max-md:p-3 max-md:space-y-3">
			<div className="flex items-start justify-between max-md:flex-wrap max-md:gap-2">
				<div>
					<h1 className="text-2xl font-semibold max-md:hidden">Konfiguracja SFY</h1>
					<PageNote id="about" title="O stronie">
						<p className="text-sm text-muted-foreground">
							Pinowanie kontraktow i deklaratywne zapytania weryfikujace dla baz klientow (SQL
							Server).
						</p>
					</PageNote>
				</div>
				<Button asChild variant="outline" size="sm" className="max-md:h-9">
					<Link
						to="/konfiguracja/sfy/przeglad"
						search={{ polaczenie: selectedConnectionId ?? undefined }}
					>
						Przeglad rozbieznosci
					</Link>
				</Button>
			</div>

			<Card>
				<CardHeader>
					<div className="flex items-center justify-between max-md:flex-wrap max-md:gap-2">
						<div>
							<CardTitle>Polaczenie</CardTitle>
							<CardDescription>
								Wybierz polaczenie do bazy klienta. Konfigurujesz pinowane kontrakty i zapytania
								osobno dla kazdego.
							</CardDescription>
						</div>
						<Button size="sm" onClick={() => setAddConnectionOpen(true)} className="max-md:h-9">
							<Plus className="mr-2 h-4 w-4" /> Dodaj polaczenie
						</Button>
					</div>
				</CardHeader>
				<CardContent>
					{connectionsQuery.isLoading ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : connectionsQuery.data && connectionsQuery.data.length > 0 ? (
						<div className="flex items-center gap-2">
							<Select
								value={selectedConnectionId ? String(selectedConnectionId) : ""}
								onValueChange={(v) => setSelectedConnectionId(Number(v))}
							>
								<SelectTrigger className="flex-1 min-w-0 max-md:[&>span]:min-w-0">
									<SelectValue placeholder="Wybierz polaczenie" />
								</SelectTrigger>
								<SelectContent>
									{connectionsQuery.data.map((c) => (
										<SelectItem key={c.id} value={String(c.id)}>
											{c.clientName} — {c.name} ({c.host}:{c.port}/{c.databaseName})
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							{selectedConnection && (
								<>
									<Button
										variant="outline"
										size="sm"
										onClick={() => setEditConnectionOpen(true)}
										title="Edytuj polaczenie"
										className="max-md:size-9"
									>
										<Pencil className="h-4 w-4" />
									</Button>
									<Button
										variant="outline"
										size="sm"
										className="text-destructive max-md:size-9"
										onClick={() => setDeleteConnectionOpen(true)}
										title="Usun polaczenie"
									>
										<Trash2 className="h-4 w-4" />
									</Button>
								</>
							)}
						</div>
					) : (
						<p className="text-sm text-muted-foreground">
							Brak polaczen. Kliknij "Dodaj polaczenie" zeby utworzyc pierwsze.
						</p>
					)}
					{selectedConnection && (
						<ConnectionNotes
							key={selectedConnection.id}
							connectionId={selectedConnection.id}
							initialNotes={selectedConnection.notes ?? ""}
						/>
					)}
				</CardContent>
			</Card>

			{selectedConnection && (
				<>
					<ProducerContractsCard
						connectionId={selectedConnection.id}
						clientId={selectedConnection.clientId}
					/>
					<QueryTemplatesCard connectionId={selectedConnection.id} />
				</>
			)}

			{addConnectionOpen && (
				<ConnectionFormDialog
					onClose={() => setAddConnectionOpen(false)}
					onSaved={(newId) => {
						setSelectedConnectionId(newId);
						setAddConnectionOpen(false);
					}}
				/>
			)}

			{editConnectionOpen && selectedConnection && (
				<ConnectionFormDialog
					existing={selectedConnection}
					onClose={() => setEditConnectionOpen(false)}
					onSaved={() => setEditConnectionOpen(false)}
				/>
			)}

			<ConfirmDialog
				open={deleteConnectionOpen}
				onOpenChange={setDeleteConnectionOpen}
				title="Usunac polaczenie?"
				description={
					selectedConnection
						? `Polaczenie "${selectedConnection.name}" zostanie usuniete wraz z przypiętymi kontraktami i query templates (CASCADE).`
						: ""
				}
				variant="destructive"
				confirmLabel="Usun"
				onConfirm={async () => {
					if (!selectedConnection) return;
					try {
						await outboundApi.deleteConnection(selectedConnection.id);
						qc.invalidateQueries({ queryKey: ["outbound", "connections", "sfy"] });
						setSelectedConnectionId(null);
					} catch (e) {
						console.error("delete connection failed", e);
					}
				}}
			/>
		</div>
	);
}

// ─── Kontrakty producenta (pinowanie + send_active) ──────────────────────────

/**
 * Lista kontraktow widocznych w SFY: domyslnie chowamy rozliczone z producentem
 * (producerSettled), chyba ze sa aktualnie pinowane (pin zawsze widoczny). Toggle
 * showSettled odslania wszystko. Sort: nierozliczone na gorze, rozliczone na dole
 * (stabilny, zachowuje kolejnosc wejsciowa w obrebie grupy).
 */
export function computeVisibleSfyContracts<
	T extends { contractId: number; producerSettled: boolean },
>(eligible: T[], pinned: { has(id: number): boolean }, showSettled: boolean): T[] {
	const filtered = showSettled
		? eligible
		: eligible.filter((c) => !c.producerSettled || pinned.has(c.contractId));
	return [...filtered].sort((a, b) => Number(a.producerSettled) - Number(b.producerSettled));
}

export function ProducerContractsCard({
	connectionId,
	clientId,
}: {
	connectionId: number;
	clientId: number;
}) {
	const qc = useQueryClient();

	const clientsQuery = useQuery({
		queryKey: ["outbound", "clients"],
		queryFn: () => outboundApi.listClients(false),
	});

	const allContractsQuery = useQuery({
		queryKey: ["contracts", "all-for-sfy"],
		queryFn: () => contractsApi.list(),
	});

	const pinnedQuery = useQuery({
		queryKey: ["sfy", "contracts", connectionId],
		queryFn: () => outboundApi.listSfyContracts(connectionId),
	});

	const producerId = useMemo(
		() => clientsQuery.data?.find((c) => c.id === clientId)?.producerId ?? null,
		[clientsQuery.data, clientId],
	);

	const eligibleContracts = useMemo(() => {
		if (!producerId) return [];
		return (allContractsQuery.data ?? []).filter((c) => c.producerId === producerId);
	}, [allContractsQuery.data, producerId]);

	const pinnedMap = useMemo(() => {
		const m = new Map<number, SfyConnectionContract>();
		for (const p of pinnedQuery.data ?? []) m.set(p.contractId, p);
		return m;
	}, [pinnedQuery.data]);

	// Domyslnie chowamy kontrakty rozliczone z producentem, chyba ze sa aktualnie
	// pinowane (pin zawsze widoczny, zeby nic nie zniknelo z konfiguracji). Toggle
	// "Pokaz rozliczone" odslania wszystko. Sort: nierozliczone na gorze.
	const [showSettled, setShowSettled] = useState(false);

	const visibleContracts = useMemo(
		() => computeVisibleSfyContracts(eligibleContracts, pinnedMap, showSettled),
		[eligibleContracts, pinnedMap, showSettled],
	);

	const hiddenSettledCount = eligibleContracts.length - visibleContracts.length;

	const pinMutation = useMutation({
		mutationFn: (contractId: number) =>
			outboundApi.pinSfyContract(connectionId, {
				contractId,
				tenantSchema: "prima",
				sendActive: true,
			}),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["sfy", "contracts", connectionId] }),
	});

	const unpinMutation = useMutation({
		mutationFn: (contractId: number) => outboundApi.unpinSfyContract(connectionId, contractId),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["sfy", "contracts", connectionId] }),
	});

	const togglePinFlag = useMutation({
		mutationFn: ({
			contractId,
			patch,
		}: {
			contractId: number;
			patch: { sendActive?: boolean; pushActive?: boolean; isMshrActive?: boolean };
		}) => outboundApi.updateSfyContract(connectionId, contractId, patch),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["sfy", "contracts", connectionId] }),
	});

	const loading = clientsQuery.isLoading || allContractsQuery.isLoading || pinnedQuery.isLoading;

	return (
		<Card>
			<CardHeader>
				<CardTitle>Kontrakty producenta</CardTitle>
				<CardDescription>
					Wszystkie kontrakty producenta przypisanego do tego polaczenia. Zaznacz "pinuj" zeby
					wlaczyc kontrakt. <strong>Kontroluj</strong> = kontrakt bierze udzial w snapshotach i
					przegladzie (verify) — kontrolujemy wszystko, co wprowadzone.{" "}
					<strong>Wyslij do klienta</strong> = swiadoma wysylka danych do klienta (apply); wymaga
					Kontroluj i wylaczenia producenta w FM1 — wysylamy tylko to, co swiadomie wybierzemy.
					Kontrakty oznaczone jako rozliczone z producentem sa domyslnie ukryte (chyba ze pinowane)
					— pokaz je przelacznikiem "Pokaz rozliczone".
				</CardDescription>
			</CardHeader>
			<CardContent>
				{loading ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : !producerId ? (
					<p className="text-sm text-muted-foreground">
						Klient tego polaczenia nie ma przypisanego producenta — przypisz go w
						/kontrakty/weryfikacja-wysylek/klienci.
					</p>
				) : eligibleContracts.length === 0 ? (
					<p className="text-sm text-muted-foreground">Brak kontraktow dla tego producenta.</p>
				) : (
					<div className="space-y-2">
						<div className="flex items-center justify-end gap-3 text-sm max-md:flex-wrap max-md:justify-start">
							{!showSettled && hiddenSettledCount > 0 && (
								<span className="text-muted-foreground">
									Ukryto rozliczonych: {hiddenSettledCount}
								</span>
							)}
							<label className="inline-flex items-center gap-2 cursor-pointer">
								<input
									type="checkbox"
									checked={showSettled}
									onChange={(e) => setShowSettled(e.target.checked)}
									className="rounded border-input"
								/>
								<span className="text-muted-foreground">Pokaz rozliczone</span>
							</label>
						</div>
						<div className="border rounded-md overflow-hidden max-md:overflow-x-auto">
							<table className="w-full text-sm">
								<thead className="bg-muted/50">
									<tr>
										<th className="text-left p-2 font-medium w-16">Pinuj</th>
										<th className="text-left p-2 font-medium">Kontrakt</th>
										<th className="text-left p-2 font-medium">Okres</th>
										<th
											className="text-left p-2 font-medium"
											title="Kontroluj: kontrakt bierze udzial w snapshotach i przegladzie (verify)"
										>
											Kontroluj
										</th>
										<th
											className="text-left p-2 font-medium"
											title="Swiadoma wysylka danych do klienta (apply). Wymaga wlaczonego Kontroluj i wylaczenia producenta w FM1."
										>
											Wyslij do klienta
										</th>
										<th
											className="text-left p-2 font-medium"
											title="Iteruje apteki tego kontraktu w funkcjach Polfarma MSHR"
										>
											MSHR
										</th>
									</tr>
								</thead>
								<tbody>
									{visibleContracts.map((c) => {
										const pinned = pinnedMap.get(c.contractId);
										const isPinned = !!pinned;
										return (
											<tr key={c.contractId} className="border-t">
												<td className="p-2">
													<input
														type="checkbox"
														checked={isPinned}
														disabled={pinMutation.isPending || unpinMutation.isPending}
														onChange={(e) => {
															if (e.target.checked) pinMutation.mutate(c.contractId);
															else unpinMutation.mutate(c.contractId);
														}}
														className="rounded border-input max-md:size-5"
													/>
												</td>
												<td className="p-2 max-md:min-w-[12rem]">
													<div className={cn(c.producerSettled && "text-muted-foreground")}>
														{c.name}
													</div>
													<div className="flex flex-wrap items-center gap-1">
														{!c.isActive && (
															<Badge variant="secondary" className="text-xs">
																nieaktywny
															</Badge>
														)}
														{c.producerSettled && (
															<Badge variant="outline" className="text-xs gap-1">
																<CheckCircle2 className="size-3" />
																rozliczony
																{c.producerSettledAt ? ` ${c.producerSettledAt}` : ""}
															</Badge>
														)}
													</div>
												</td>
												<td className="p-2 font-mono text-xs">
													{c.year}/{c.quarter}
												</td>
												<td className="p-2">
													{isPinned ? (
														<label className="inline-flex items-center gap-2 cursor-pointer">
															<input
																type="checkbox"
																checked={pinned.sendActive}
																disabled={togglePinFlag.isPending}
																onChange={(e) =>
																	togglePinFlag.mutate({
																		contractId: c.contractId,
																		patch: { sendActive: e.target.checked },
																	})
																}
																className="rounded border-input"
															/>
															{pinned.sendActive ? (
																<Badge variant="default">aktywne</Badge>
															) : (
																<Badge variant="secondary">wstrzymane</Badge>
															)}
														</label>
													) : (
														<span className="text-xs text-muted-foreground">—</span>
													)}
												</td>
												<td className="p-2">
													{isPinned ? (
														<label className="inline-flex items-center gap-2 cursor-pointer">
															<input
																type="checkbox"
																checked={pinned.pushActive}
																disabled={togglePinFlag.isPending || !pinned.sendActive}
																onChange={(e) =>
																	togglePinFlag.mutate({
																		contractId: c.contractId,
																		patch: { pushActive: e.target.checked },
																	})
																}
																className="rounded border-input"
																title="Swiadoma wysylka do klienta (apply). Wymaga Kontroluj. Upewnij sie, ze producent jest wylaczony w FM1."
															/>
															{pinned.pushActive ? (
																<Badge variant="default">wysylam</Badge>
															) : (
																<Badge variant="secondary">nie wysylam</Badge>
															)}
														</label>
													) : (
														<span className="text-xs text-muted-foreground">—</span>
													)}
												</td>
												<td className="p-2">
													{isPinned ? (
														<input
															type="checkbox"
															checked={pinned.isMshrActive}
															disabled={togglePinFlag.isPending}
															onChange={(e) =>
																togglePinFlag.mutate({
																	contractId: c.contractId,
																	patch: { isMshrActive: e.target.checked },
																})
															}
															className="rounded border-input max-md:size-5"
															title="Apteki tego kontraktu beda iterowane w funkcjach Polfarma MSHR"
														/>
													) : (
														<span className="text-xs text-muted-foreground">—</span>
													)}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					</div>
				)}
				<Fm1ComparePanel contracts={visibleContracts} />
			</CardContent>
		</Card>
	);
}

/**
 * Porownanie kontraktu FM2 z odpowiadajacym mu kontraktem FM1 — czy po obu stronach
 * sa te same apteki i te same blozy.
 *
 * Zrodlem sa tabele `fm1_*` synchronizowane przez robota. Mimo nazwy `fm1_teva_*`
 * sa GENERYCZNE: kluczem jest `contract_id`, wiec dotycza kazdego producenta.
 * Tylko odczyt — nic nie zapisuje ani nie wysyla.
 */
function Fm1ComparePanel({
	contracts,
}: {
	contracts: { contractId: number; name: string; year: number; quarter: string }[];
}) {
	const [fm2Id, setFm2Id] = useState<string>("");
	// Druga strona FM2: w FM1 producent mial jeden kontrakt, a FM2 dzieli go na
	// apteki wlasne (AW) i franczyzowe (AF). Bez tego apteki z drugiej polowki
	// wygladaja na „nie przenieslismy".
	const [fm2Id2, setFm2Id2] = useState<string>("");
	const [fm1Id, setFm1Id] = useState<string>("");
	const [error, setError] = useState<string | null>(null);

	const fm1Query = useQuery({
		queryKey: ["sfy", "fm1-contracts"],
		queryFn: () => outboundApi.listFm1Contracts(),
		staleTime: 10 * 60 * 1000,
	});
	const compare = useMutation({
		mutationFn: () =>
			outboundApi.compareWithFm1(Number(fm2Id), Number(fm1Id), fm2Id2 ? Number(fm2Id2) : undefined),
		onSuccess: () => setError(null),
		onError: (e: unknown) => setError(e instanceof Error ? e.message : "Blad porownania z FM1"),
	});
	// Eksport pyta backend PONOWNIE z `full=true`: na ekranie listy sa obciete do
	// 200 pozycji, a plik ma zawierac komplet — inaczej klamalby dokladnie tam,
	// gdzie roznic jest najwiecej.
	const eksport = useMutation({
		mutationFn: async () => {
			const pelny = await outboundApi.compareWithFm1(
				Number(fm2Id),
				Number(fm1Id),
				fm2Id2 ? Number(fm2Id2) : undefined,
				true,
			);
			await exportToExcel({
				sheets: buildFm1CompareSheets(pelny),
				fileName: fm1CompareFileName(pelny),
			});
		},
		onSuccess: () => setError(null),
		onError: (e: unknown) => setError(e instanceof Error ? e.message : "Blad eksportu do Excela"),
	});
	const canCompare = fm2Id !== "" && fm1Id !== "" && !compare.isPending;
	const brakFm1 = !fm1Query.isLoading && (fm1Query.data?.length ?? 0) === 0;

	return (
		<div className="mt-4 space-y-3 rounded-md border border-border p-3">
			<div>
				<div className="text-sm font-medium">Porownanie z kontraktem FM1</div>
				<div className="text-xs text-muted-foreground">
					Czy kontrakt ma po obu stronach te same apteki i te same blozy. Tylko odczyt. Jeden
					kontrakt FM1 bywa w FM2 rozbity na apteki wlasne i franczyzowe — wtedy wskaz obie strony.
				</div>
			</div>
			{error && <NoticeBanner tone="error" title="Blad" description={error} />}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
				<div className="space-y-1 min-w-0">
					<Label>Kontrakt FM2</Label>
					<select
						className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm max-md:h-9"
						value={fm2Id}
						onChange={(e) => {
							setFm2Id(e.target.value);
							// Druga strona nie moze zostac na tym samym kontrakcie — jej opcja
							// znika z listy, a wartosc zostalaby niewidoczna w polu.
							if (e.target.value === fm2Id2) setFm2Id2("");
						}}
					>
						<option value="">Wybierz kontrakt</option>
						{contracts.map((c) => (
							<option key={c.contractId} value={c.contractId}>
								{c.name} ({c.year}/{c.quarter})
							</option>
						))}
					</select>
				</div>
				<div className="space-y-1 min-w-0">
					<Label>Drugi kontrakt FM2 (opcjonalnie)</Label>
					<select
						className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm max-md:h-9"
						value={fm2Id2}
						onChange={(e) => setFm2Id2(e.target.value)}
					>
						<option value="">Brak — porownuj tylko jeden</option>
						{contracts
							.filter((c) => String(c.contractId) !== fm2Id)
							.map((c) => (
								<option key={c.contractId} value={c.contractId}>
									{c.name} ({c.year}/{c.quarter})
								</option>
							))}
					</select>
				</div>
				<div className="space-y-1 min-w-0">
					<Label>Kontrakt FM1</Label>
					<select
						className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm max-md:h-9"
						value={fm1Id}
						onChange={(e) => setFm1Id(e.target.value)}
						disabled={fm1Query.isLoading || brakFm1}
					>
						<option value="">
							{fm1Query.isLoading
								? "Wczytuje..."
								: brakFm1
									? "Brak kontraktow FM1 — robot ich jeszcze nie zsynchronizowal"
									: "Wybierz kontrakt"}
						</option>
						{(fm1Query.data ?? []).map((c) => (
							<option key={c.id} value={c.id}>
								{c.name ?? `#${c.id}`} · {c.apteki} aptek · {c.blozy} blozow
							</option>
						))}
					</select>
				</div>
			</div>
			<div className="flex items-center gap-2 max-md:flex-wrap">
				<Button
					size="sm"
					onClick={() => compare.mutate()}
					disabled={!canCompare}
					className="max-md:h-9"
				>
					{compare.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
					Porownaj
				</Button>
				{compare.data && (
					<Button
						size="sm"
						variant="outline"
						onClick={() => eksport.mutate()}
						disabled={eksport.isPending}
						className="max-md:h-9"
					>
						{eksport.isPending ? (
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						) : (
							<FileDown className="mr-2 h-4 w-4" />
						)}
						Pobierz Excel z roznicami
					</Button>
				)}
			</div>
			{compare.data && <Fm1CompareResult cmp={compare.data} />}
		</div>
	);
}

/** Wynik: dwie symetryczne tabelki + notatki o pulapkach na kluczach. */
function Fm1CompareResult({ cmp }: { cmp: Fm1Comparison }) {
	const blok = (tytul: string, d: SfySetDiff) => (
		<div className="space-y-1">
			<div className="text-xs">
				<span className="font-medium">{tytul}</span> ·{" "}
				<span className="text-status-success-text">{d.common} wspolnych</span> ·{" "}
				<span className={d.onlyFm1Count > 0 ? "text-status-warning-text" : "text-muted-foreground"}>
					{d.onlyFm1Count} tylko w FM1
				</span>{" "}
				·{" "}
				<span className={d.onlyFm2Count > 0 ? "text-status-warning-text" : "text-muted-foreground"}>
					{d.onlyFm2Count} tylko w FM2
				</span>
				{d.truncated && <span className="ml-1 text-muted-foreground">· listy obciete do 200</span>}
			</div>
			{(d.onlyFm1.length > 0 || d.onlyFm2.length > 0) && (
				<div className="grid grid-cols-1 md:grid-cols-2 gap-2">
					{(
						[
							["Tylko w FM1 — nie przenieslismy", d.onlyFm1],
							["Tylko w FM2 — nadmiar wzgledem FM1", d.onlyFm2],
						] as const
					).map(([naglowek, lista]) => (
						<div key={naglowek} className="rounded-md border border-border">
							<div className="border-b border-border bg-muted/40 px-2 py-1 text-[11px] text-muted-foreground">
								{naglowek} ({lista.length})
							</div>
							<div className="max-h-40 overflow-auto px-2 py-1 font-mono text-[11px]">
								{lista.length === 0 ? (
									<span className="text-status-success-text">—</span>
								) : (
									lista.map((v) => <div key={v}>{v}</div>)
								)}
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
	return (
		<div className="space-y-3">
			<div className="text-xs text-muted-foreground">
				FM2: <span className="font-medium">{cmp.fm2ContractName}</span> ↔ FM1:{" "}
				<span className="font-medium">{cmp.fm1ContractName ?? `#${cmp.fm1ContractId}`}</span>
			</div>
			{cmp.notes.map((n) => (
				<div
					key={n}
					className="rounded-md border border-status-warning-border bg-status-warning-bg px-2 py-1.5 text-xs text-status-warning-text"
				>
					{n}
				</div>
			))}
			{blok("Apteki", cmp.pharmacies)}
			{blok("Blozy", cmp.blozy)}
		</div>
	);
}

// ─── Dodawanie polaczenia (inline form) ──────────────────────────────────────

interface ConnectionFormState {
	clientId: string;
	name: string;
	dbType: string;
	host: string;
	port: string;
	databaseName: string;
	username: string;
	secretOcid: string;
	sslMode: string;
	active: boolean;
}

const DEFAULT_CONNECTION_FORM: ConnectionFormState = {
	clientId: "",
	name: "",
	dbType: "sqlserver",
	host: "djfarm.softforyou.pl",
	port: "49998",
	databaseName: "PSDT_DJFARM_",
	username: "djfarmacja",
	secretOcid: "env:HERBAPOLP_SQLSERVER_PASSWORD",
	sslMode: "off",
	active: true,
};

function ConnectionFormDialog({
	existing,
	onClose,
	onSaved,
}: {
	existing?: OutboundConnection | null;
	onClose: () => void;
	onSaved: (id: number) => void;
}) {
	const qc = useQueryClient();
	const isEdit = !!existing;
	const [form, setForm] = useState<ConnectionFormState>(() =>
		existing
			? {
					clientId: String(existing.clientId),
					name: existing.name,
					dbType: existing.dbType,
					host: existing.host,
					port: String(existing.port),
					databaseName: existing.databaseName,
					username: existing.username,
					secretOcid: existing.secretOcid,
					sslMode: existing.sslMode ?? "off",
					active: existing.active,
				}
			: DEFAULT_CONNECTION_FORM,
	);
	const [error, setError] = useState<string | null>(null);
	const [addProducerOpen, setAddProducerOpen] = useState(false);

	const clientsQuery = useQuery({
		queryKey: ["outbound", "clients"],
		queryFn: () => outboundApi.listClients(false),
	});

	function patch(p: Partial<ConnectionFormState>) {
		setForm((f) => ({ ...f, ...p }));
	}

	const saveMutation = useMutation({
		mutationFn: () => {
			const payload = {
				clientId: Number(form.clientId),
				name: form.name.trim(),
				dbType: form.dbType.trim(),
				host: form.host.trim(),
				port: Number(form.port) || 0,
				databaseName: form.databaseName.trim(),
				username: form.username.trim(),
				secretOcid: form.secretOcid.trim(),
				sslMode: form.sslMode.trim() || null,
				active: form.active,
			};
			return existing
				? outboundApi.updateConnection(existing.id, payload)
				: outboundApi.createConnection(payload);
		},
		onSuccess: (saved) => {
			qc.invalidateQueries({ queryKey: ["outbound", "connections", "sfy"] });
			onSaved(saved.id);
		},
		onError: (e: unknown) => setError(e instanceof Error ? e.message : "Blad zapisu"),
	});

	const canSubmit =
		!!form.clientId &&
		form.name.trim().length > 0 &&
		form.host.trim().length > 0 &&
		Number(form.port) > 0 &&
		form.databaseName.trim().length > 0 &&
		form.username.trim().length > 0 &&
		form.secretOcid.trim().length > 0;

	return (
		<Dialog open onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="max-w-2xl">
				<DialogHeader>
					<DialogTitle>
						{isEdit ? `Edycja polaczenia: ${existing?.name}` : "Dodaj polaczenie SQL Server"}
					</DialogTitle>
					<DialogDescription>
						Polaczenie do bazy klienta. Secret OCID w dev: <code>env:OUTBOUND_SECRET_NAZWA</code>{" "}
						(inne nazwy zmiennych sa odrzucane), w prod: OCID z OCI Vault.
					</DialogDescription>
				</DialogHeader>
				<DialogBody className="space-y-4">
					{error && (
						<NoticeBanner
							tone="error"
							title="Blad zapisu"
							description={error}
							onDismiss={() => setError(null)}
						/>
					)}

					<div className="space-y-1">
						<div className="flex items-center justify-between">
							<Label>Producent</Label>
							<Button
								variant="ghost"
								size="sm"
								className="h-5 px-2 text-xs max-md:h-9"
								onClick={() => setAddProducerOpen(true)}
							>
								<Plus className="mr-1 h-3 w-3" /> Nowy
							</Button>
						</div>
						<Select
							value={form.clientId}
							onValueChange={(v) => patch({ clientId: v })}
							disabled={clientsQuery.isLoading}
						>
							<SelectTrigger>
								<SelectValue placeholder="wybierz producenta" />
							</SelectTrigger>
							<SelectContent>
								{(clientsQuery.data ?? [])
									.filter((c) => c.producerId != null)
									.map((c) => (
										<SelectItem key={c.id} value={String(c.id)}>
											{c.producerName ?? `klient #${c.id}`}
										</SelectItem>
									))}
							</SelectContent>
						</Select>
					</div>
					<div className="space-y-1">
						<Label>Nazwa polaczenia</Label>
						<Input
							value={form.name}
							onChange={(e) => patch({ name: e.target.value })}
							placeholder="prod-main"
						/>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div className="space-y-1">
							<Label>db_type</Label>
							<Select value={form.dbType} onValueChange={(v) => patch({ dbType: v })}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="sqlserver">sqlserver</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-1">
							<Label>ssl_mode</Label>
							<Select value={form.sslMode} onValueChange={(v) => patch({ sslMode: v })}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="trust_cert">trust_cert</SelectItem>
									<SelectItem value="encrypt">encrypt</SelectItem>
									<SelectItem value="off">off</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-1 md:col-span-2">
							<Label>host</Label>
							<Input
								value={form.host}
								onChange={(e) => patch({ host: e.target.value })}
								placeholder="10.0.0.5"
							/>
						</div>
						<div className="space-y-1">
							<Label>port</Label>
							<Input
								type="number"
								value={form.port}
								onChange={(e) => patch({ port: e.target.value })}
								placeholder="1433"
							/>
						</div>
						<div className="space-y-1">
							<Label>database_name</Label>
							<Input
								value={form.databaseName}
								onChange={(e) => patch({ databaseName: e.target.value })}
								placeholder="HerbapolDB"
							/>
						</div>
						<div className="space-y-1">
							<Label>username</Label>
							<Input
								value={form.username}
								onChange={(e) => patch({ username: e.target.value })}
								placeholder="farmaster_ro"
							/>
						</div>
						<div className="space-y-1">
							<Label>secret_ocid</Label>
							<Input
								value={form.secretOcid}
								onChange={(e) => patch({ secretOcid: e.target.value })}
								placeholder="env:OUTBOUND_SECRET_HERBAPOL"
							/>
						</div>
					</div>

					<label className="flex items-center gap-2 cursor-pointer text-sm">
						<input
							type="checkbox"
							checked={form.active}
							onChange={(e) => patch({ active: e.target.checked })}
							className="rounded border-input"
						/>
						Aktywne
					</label>
				</DialogBody>
				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Anuluj
					</Button>
					<Button
						onClick={() => saveMutation.mutate()}
						disabled={!canSubmit || saveMutation.isPending}
					>
						{saveMutation.isPending ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : isEdit ? (
							"Zapisz"
						) : (
							"Utworz"
						)}
					</Button>
				</DialogFooter>
			</DialogContent>

			{addProducerOpen && (
				<AddProducerDialog
					existingProducerIds={
						new Set(
							(clientsQuery.data ?? [])
								.map((c) => c.producerId)
								.filter((p): p is number => p != null),
						)
					}
					onClose={() => setAddProducerOpen(false)}
					onCreated={(newClientId) => {
						setAddProducerOpen(false);
						patch({ clientId: String(newClientId) });
					}}
				/>
			)}
		</Dialog>
	);
}

// ─── Dodawanie producenta (outbound_client dla producer_id) ───────────────────

function AddProducerDialog({
	existingProducerIds,
	onClose,
	onCreated,
}: {
	existingProducerIds: Set<number>;
	onClose: () => void;
	onCreated: (clientId: number) => void;
}) {
	const qc = useQueryClient();
	const [producerId, setProducerId] = useState<string>("");
	const [error, setError] = useState<string | null>(null);
	// callback ref ze stanem — re-renderuje gdy element wmontuje sie w drzewo,
	// dzieki czemu ProducerSelector dostanie poprawny portalContainer i input
	// wyszukiwarki przechodzi przez Dialog FocusScope.
	const [dialogEl, setDialogEl] = useState<HTMLDivElement | null>(null);

	const producersQuery = useQuery({
		queryKey: ["outbound", "producers"],
		queryFn: () => outboundApi.listProducers(),
	});

	const available = useMemo(
		() =>
			(producersQuery.data ?? [])
				.filter((p) => !existingProducerIds.has(p.id))
				.map((p) => ({ id: p.id, nazwa: p.name })),
		[producersQuery.data, existingProducerIds],
	);

	const createMutation = useMutation({
		mutationFn: () => outboundApi.createClientForProducer(Number(producerId)),
		onSuccess: (client) => {
			qc.invalidateQueries({ queryKey: ["outbound", "clients"] });
			onCreated(client.id);
		},
		onError: (e: unknown) => setError(e instanceof Error ? e.message : "Blad tworzenia"),
	});

	return (
		<Dialog open onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="max-w-md" ref={setDialogEl}>
				<DialogHeader>
					<DialogTitle>Dodaj producenta do listy SFY</DialogTitle>
					<DialogDescription>
						Tworzy outbound_client dla wybranego producenta z prima.producent. Producenci
						zsynchronizowani przez Robota z Kamsoftu — jezeli producent nie pojawia sie na liscie,
						zgloszenie do Robota.
					</DialogDescription>
				</DialogHeader>
				<DialogBody className="space-y-4">
					{error && (
						<NoticeBanner
							tone="error"
							title="Blad"
							description={error}
							onDismiss={() => setError(null)}
						/>
					)}
					<div className="space-y-1">
						<Label>Producent</Label>
						<ProducerSelector
							producers={available}
							value={producerId ? Number(producerId) : null}
							onChange={(v) => setProducerId(v == null ? "" : String(v))}
							placeholder="wybierz producenta"
							portalContainer={dialogEl}
						/>
						{available.length === 0 && !producersQuery.isLoading && (
							<p className="text-xs text-muted-foreground">
								Wszyscy producenci z prima.producent juz maja outbound_client.
							</p>
						)}
					</div>
				</DialogBody>
				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Anuluj
					</Button>
					<Button
						onClick={() => createMutation.mutate()}
						disabled={!producerId || createMutation.isPending}
					>
						{createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Utworz"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ─── Query templates ──────────────────────────────────────────────────────────

// Kanoniczny zestaw zapytan per producent. Generator „Wygeneruj zapytania" tworzy
// BRAKUJACE (po nazwie) jako NIEAKTYWNE — do recznej weryfikacji i aktywacji.
const CANONICAL_QUERIES: {
	template: string;
	suffix: string;
	mode: SfySnapshotMode;
	name: string;
}[] = [
	{ template: "towr", suffix: "TOWR", mode: "row", name: "Towary" },
	{ template: "firm", suffix: "FIRM", mode: "row", name: "Apteki" },
	{ template: "dedf", suffix: "DEDF", mode: "row", name: "Definicje dokumentow" },
	{ template: "pers", suffix: "PERS", mode: "row", name: "Pracownicy" },
	{ template: "dost", suffix: "DOST", mode: "row", name: "Dostawcy" },
	{ template: "korz", suffix: "KORZ", mode: "row", name: "Korekty/zwroty" },
	{ template: "sprz", suffix: "SPRZ", mode: "aggregate", name: "Sprzedaz" },
	{ template: "kzak", suffix: "KZAK", mode: "aggregate", name: "Zakup (ilosc)" },
	{ template: "kzak_stan", suffix: "KZAK", mode: "aggregate", name: "Zakup (stan)" },
	// Kompletnosc zakupow kontraktowych jako zwykly row (zastepuje kzak completeness):
	// zakup referowany przez korekte/sprzedaz z okresu, scope po naszej stronie (bloz).
	{ template: "kzak_korz", suffix: "KZAK", mode: "row", name: "Zakup z korekt" },
	{ template: "kzak_sprz", suffix: "KZAK", mode: "row", name: "Zakup ze sprzedazy" },
	{ template: "dokf", suffix: "DOKF", mode: "row", name: "Dokumenty" },
	{ template: "dokf", suffix: "DOKF", mode: "completeness", name: "Dokumenty (completeness)" },
];

/** Kwalifikowana nazwa tabeli klienta dla (prefix, suffix) z introspekcji; null gdy brak. */
function findClientTable(
	schema: OutboundSchemaSnapshot,
	prefix: string,
	suffix: string,
): string | null {
	const exact = `${prefix}${suffix}`.toLowerCase();
	const byExact = schema.tables.find((t) => t.tableName.toLowerCase() === exact);
	if (byExact) return `${byExact.tableSchema}.${byExact.tableName}`;
	const bySuffix = schema.tables.find(
		(t) =>
			t.tableName.toLowerCase().endsWith(suffix.toLowerCase()) &&
			t.tableName.toLowerCase().startsWith(prefix.toLowerCase()),
	);
	return bySuffix ? `${bySuffix.tableSchema}.${bySuffix.tableName}` : null;
}

function QueryTemplatesCard({ connectionId }: { connectionId: number }) {
	const qc = useQueryClient();
	const [editTarget, setEditTarget] = useState<SfyConnectionQuery | "new" | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<SfyConnectionQuery | null>(null);
	const [verifyTarget, setVerifyTarget] = useState<SfyConnectionQuery | null>(null);

	const queriesQuery = useQuery({
		queryKey: ["sfy", "queries", connectionId],
		queryFn: () => outboundApi.listSfyQueries(connectionId),
	});

	const deleteMutation = useMutation({
		mutationFn: (q: SfyConnectionQuery) => outboundApi.deleteSfyQuery(connectionId, q.id),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["sfy", "queries", connectionId] }),
	});

	const [genSummary, setGenSummary] = useState<string | null>(null);
	const generateMutation = useMutation({
		mutationFn: async () => {
			const schema = await outboundApi.getConnectionSchema(connectionId);
			const schemaTables = schema.tables.map((t) => `${t.tableSchema}.${t.tableName}`);
			const prefix = deriveClientPrefix("", schemaTables);
			if (!prefix)
				throw new Error(
					"Nie moge wyliczyc prefiksu tabel klienta (brak tabeli *SPRZ). Sprawdz polaczenie/introspekcje.",
				);
			const existing = new Set((queriesQuery.data ?? []).map((q) => q.name.trim().toLowerCase()));
			let created = 0;
			const noTable: string[] = [];
			const warnings: string[] = [];
			const errors: string[] = [];
			for (const entry of CANONICAL_QUERIES) {
				if (existing.has(entry.name.toLowerCase())) continue;
				const template = getTemplate(entry.template);
				if (!template) continue;
				const dest = findClientTable(schema, prefix, entry.suffix);
				if (!dest) {
					noTable.push(entry.name);
					continue;
				}
				const cols = clientColumnsFor(schema, dest).map((c) => c.columnName);
				const checked = defaultCheckedColumns(template, cols);
				// forAggregate: agregat kubelkuje po (idfirm, bloz07, miesiac), wiec projekcja
				// MUSI niesc te aliasy — inaczej backend odrzuca zapis i zapytanie w ogole
				// nie powstaje. Generator pojedynczego zapytania robil to od 5bdeb8f1,
				// zbiorczy nie, wiec „Sprzedaz" cicho wypadalo z kompletu.
				const { sourceSql, mappings } = buildSourceSqlAndMappings(
					template,
					checked,
					entry.mode === "aggregate",
				);
				const colsLower = new Set(cols.map((c) => c.toLowerCase()));
				const missing = template.columns.filter((c) => !colsLower.has(c.alias.toLowerCase()));
				if (missing.length > 0) warnings.push(`${entry.name} (brak ${missing.length} kol.)`);
				const agg = entry.mode === "aggregate" ? buildAggregate(template, prefix) : null;
				const payload: UpsertSfyQueryRequest = {
					name: entry.name,
					ourTable: entry.template,
					destinationTable: dest,
					sourceSql,
					sqlFarmasterOld: null,
					columnMappings: mappings,
					active: false,
					pushActive: false,
					autoApply: false,
					autoMaxUpserts: 20000,
					autoMaxPerFirm: 100,
					notes: "Auto-wygenerowane — zweryfikuj i aktywuj.",
					aggregateSourceSql: agg?.aggregateSourceSql ?? null,
					aggregateDestSql: agg?.aggregateDestSql ?? null,
					aggregateMatchKeys: agg?.matchKeys ?? [],
					snapshotMode: entry.mode,
				};
				try {
					await outboundApi.createSfyQuery(connectionId, payload);
					created += 1;
				} catch (e) {
					errors.push(`${entry.name}: ${e instanceof Error ? e.message : "blad"}`);
				}
			}
			return { created, noTable, warnings, errors };
		},
		onSuccess: (r) => {
			qc.invalidateQueries({ queryKey: ["sfy", "queries", connectionId] });
			// Nazwy zapytan, ktore NIE powstaly, ida na poczatek i osobnym zdaniem.
			// Wczesniej "bledy:" wisialo na koncu jednego ciagu obok "ostrzezenia:",
			// wiec zapytanie wymienione w obu (Sprzedaz: 2 brakujace kolumny ORAZ blad
			// zapisu) wygladalo na utworzone — a go nie bylo na liscie.
			const parts: string[] = [];
			if (r.errors.length) {
				parts.push(`NIE UTWORZONO ${r.errors.length}: ${r.errors.join("; ")}`);
			}
			parts.push(`Utworzono ${r.created} (NIEAKTYWNE — zweryfikuj i aktywuj)`);
			if (r.noTable.length) parts.push(`brak tabeli u klienta: ${r.noTable.join(", ")}`);
			if (r.warnings.length) parts.push(`ostrzezenia: ${r.warnings.join("; ")}`);
			setGenSummary(parts.join(" · "));
		},
		onError: (e: unknown) => setGenSummary(e instanceof Error ? e.message : "Blad generowania"),
	});

	return (
		<Card>
			<CardHeader>
				<div className="flex items-center justify-between max-md:flex-wrap max-md:gap-2">
					<div>
						<CardTitle>Zapytania (query templates)</CardTitle>
						<CardDescription>
							Deklaratywne definicje: kolumny z prima.&lt;our_table&gt; -&gt; destination_table
							klienta + mapowanie blozu (direct lub animization).
						</CardDescription>
					</div>
					<div className="flex items-center gap-2 max-md:flex-wrap max-md:[&>button]:h-9">
						<InfoDialog
							triggerLabel="Legenda"
							title="Jak konfigurowac zapytania SFY"
							description="Co znacza ustawienia i jakie zapytania mozna tworzyc per tabela."
						>
							<InfoDialogSection title="Czym jest zapytanie">
								<p>
									Deklaratywna definicja: kolumny z <code>prima.&lt;our_table&gt;</code> mapowane na
									tabele klienta <code>destination_table</code> (np. <code>dbo.HerbapolpKZAK</code>
									).
									<strong> Match keys</strong> = klucz naturalny wiersza po ktorym dopasowujemy i
									upsertujemy — zwykle <code>(idfirm, id)</code>, tabela <code>firm</code> = samo{" "}
									<code>id</code>.
								</p>
							</InfoDialogSection>

							<InfoDialogSection title="Tryby snapshot (Snapshot)">
								<ul>
									<li>
										<strong>off</strong> — poza monitoringiem (nie liczy snapshotu).
									</li>
									<li>
										<strong>row</strong> — porownanie wiersz-po-wierszu WSZYSTKICH kolumn. Dla
										malych / slownikowych: <code>towr, firm, dedf, pers</code>. Wymaga zmapowania
										wszystkich kolumn projekcji.
									</li>
									<li>
										<strong>aggregate</strong> — sumy/liczby per kubelek (duze tabele:{" "}
										<code>sprz, kzak</code>). Wymaga SQL agregatu (nasz PG + T-SQL klienta) i kluczy
										agregacji. Detekcja tania; naprawa rozwija tylko rozjechane kubelki do detalu.
									</li>
									<li>
										<strong>completeness</strong> — domkniecie referencyjne (<code>dokf, kzak</code>
										): dosyla wiersze referowane przez klienta, ktorych u niego brak. Okno 2 lata.
									</li>
								</ul>
							</InfoDialogSection>

							<InfoDialogSection title="Aggregate — warianty (kzak/sprz)">
								<ul>
									<li>
										<strong>ilosc</strong> — <code>SUM(ilzkl), COUNT(*)</code> GROUP BY (idfirm,
										bloz07, miesiac), okno = kwartal kontraktu. Kompletnosc zakupow okresu.
									</li>
									<li>
										<strong>stan</strong> — <code>SUM(ilakt)</code> GROUP BY (idfirm, bloz07, wskus,
										bufor), <code>WHERE ilakt&lt;&gt;0</code>, okno <code>:STOCK_FROM</code> (2
										lata). Synchronizuje stan u klienta; grupowanie po wskus/bufor lapie zmiane tych
										kolumn.
									</li>
								</ul>
							</InfoDialogSection>

							<InfoDialogSection title="Completeness — przez jakie tabele">
								<ul>
									<li>
										<strong>dokf</strong> ← referencje z <strong>KZAK, KORZ, SPRZ</strong> klienta
										(po <code>iddokf</code>). Braki INSERT + roznice UPDATE.
									</li>
									<li>
										<strong>kzak</strong> ← referencje z <strong>SPRZ, KORZ</strong> klienta (po{" "}
										<code>idkzak</code>). <strong>Tylko INSERT brakow</strong> (anti-join po stronie
										klienta) — stan/update ogarnia osobny agregat „kzak stan".
									</li>
								</ul>
							</InfoDialogSection>

							<InfoDialogSection title="Placeholdery w SQL">
								<ul>
									<li>
										<code>:UNITCODES</code> — apteki kontraktu; <code>:BLOZ07</code> — blozy
										koszyka.
									</li>
									<li>
										<code>:CONTRACT_DATE_FROM</code> / <code>:CONTRACT_DATE_TO</code> — kwartal
										kontraktu (auto z year+quarter).
									</li>
									<li>
										<code>:STOCK_FROM</code> — dzis - 2 lata (okno stanu kzak; boundowanie
										hipertabeli).
									</li>
									<li>
										<code>:MAXDATE</code>, <code>:CONTRACT_ID</code> — pomocnicze.
									</li>
								</ul>
							</InfoDialogSection>

							<InfoDialogSection title="Mapowanie kolumn i anonimizacja">
								<p>
									Kazda kolumna: <code>source_alias</code> (u nas) → <code>dest_column</code> (u
									klienta, mixed case). <strong>match key</strong> = element klucza naturalnego.{" "}
									<strong>anonimize</strong> = tlumaczenie bloz przez mape per producent — dozwolone
									TYLKO na match key (row-mode); w kzak/sprz klucz = id+idfirm, wiec nie wystepuje.
								</p>
							</InfoDialogSection>

							<InfoDialogSection title="Wysylka do klienta">
								<p>
									<strong>Kontroluj</strong> (send_active) = kontrakt bierze udzial w snapshotach.{" "}
									<strong>Wyslij do klienta</strong> (push_active) = swiadoma wysylka (apply). Apply
									= idempotentny upsert (<code>IF NOT EXISTS INSERT ELSE UPDATE</code>),{" "}
									<strong>zero DELETE</strong>. Zawsze najpierw <strong>Dry-run</strong>, potem
									realny — z run-logiem.
								</p>
							</InfoDialogSection>

							<InfoDialogSection title="Rekomendowany zestaw per tabela">
								<ul>
									<li>
										<code>towr, firm, dedf, pers</code> — <strong>row</strong>.
									</li>
									<li>
										<code>sprz</code> — <strong>aggregate</strong> (ilosc).
									</li>
									<li>
										<code>kzak</code> — <strong>aggregate ilosc</strong> +{" "}
										<strong>aggregate stan</strong> + <strong>completeness</strong> (via sprz/korz).
									</li>
									<li>
										<code>dokf</code> — podstawowe (aggregate/row) + <strong>completeness</strong>{" "}
										(via kzak/korz/sprz).
									</li>
								</ul>
							</InfoDialogSection>
						</InfoDialog>
						<Button
							variant="outline"
							size="sm"
							onClick={() => {
								setGenSummary(null);
								generateMutation.mutate();
							}}
							disabled={generateMutation.isPending}
							title="Tworzy brakujace zapytania (kanoniczny zestaw) jako NIEAKTYWNE — do weryfikacji i aktywacji"
						>
							{generateMutation.isPending ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<Sparkles className="mr-2 h-4 w-4" />
							)}
							Wygeneruj zapytania
						</Button>
						<Button onClick={() => setEditTarget("new")} size="sm">
							<Plus className="mr-2 h-4 w-4" /> Nowe zapytanie
						</Button>
					</div>
				</div>
				{genSummary && (
					<div className="pt-2">
						<NoticeBanner tone="info" title="Wygenerowano zapytania" description={genSummary} />
					</div>
				)}
			</CardHeader>
			<CardContent>
				{queriesQuery.isLoading ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : queriesQuery.data && queriesQuery.data.length > 0 ? (
					<div className="border rounded-md overflow-hidden max-md:overflow-x-auto">
						<table className="w-full text-sm">
							<thead className="bg-muted/50">
								<tr>
									<th className="text-left p-2 font-medium">Nazwa</th>
									<th className="text-left p-2 font-medium">our_table</th>
									<th className="text-left p-2 font-medium">destination_table</th>
									<th className="text-left p-2 font-medium">Mapowan</th>
									<th className="text-left p-2 font-medium">Match keys</th>
									<th className="text-left p-2 font-medium">Snapshot</th>
									<th className="text-left p-2 font-medium">Status</th>
									<th className="text-right p-2 font-medium">Akcje</th>
								</tr>
							</thead>
							<tbody>
								{queriesQuery.data.map((q) => (
									<tr key={q.id} className="border-t">
										<td className="p-2 max-md:min-w-[10rem]">
											{q.name}
											<TemplateDriftBadge query={q} />
										</td>
										<td className="p-2 font-mono text-xs">{q.ourTable}</td>
										<td className="p-2 font-mono text-xs">{q.destinationTable}</td>
										<td className="p-2">{q.columnMappings.length}</td>
										<td className="p-2 font-mono text-xs">
											{q.columnMappings
												.filter((m) => m.isMatchKey)
												.map((m) => m.sourceAlias + (m.anonimize ? "*" : ""))
												.join(", ")}
										</td>
										<td className="p-2 font-mono text-xs">{q.snapshotMode}</td>
										<td className="p-2">
											{!q.active ? (
												<Badge variant="secondary">Wylaczone</Badge>
											) : q.pushActive ? (
												<span
													title={
														q.autoApply
															? `Aktywne, WYSYLANE do klienta i AUTOMAT (nocny cron, limit ${q.autoMaxPerFirm} wpisow/apteke, calkowity ${q.autoMaxUpserts})`
															: "Aktywne i WYSYLANE do klienta (insert/update) — wymaga tez wlaczonej wysylki na kontrakcie"
													}
												>
													<Badge className="border border-status-error-border bg-status-error-bg text-status-error-text">
														Aktywne · wysylane{q.autoApply ? " · automat" : ""}
													</Badge>
												</span>
											) : (
												<span title="Aktywne tylko do kontroli (snapshot/verify/przeglad) — NIE wysylane do klienta">
													<Badge className="border border-status-success-border bg-status-success-bg text-status-success-text">
														Aktywne · kontrola
													</Badge>
												</span>
											)}
										</td>
										<td className="p-2 text-right max-md:whitespace-nowrap max-md:[&>button]:size-9">
											<Button
												variant="ghost"
												size="sm"
												onClick={() => setVerifyTarget(q)}
												title="Uruchom verify"
											>
												<Play className="h-4 w-4" />
											</Button>
											<Button variant="ghost" size="sm" onClick={() => setEditTarget(q)}>
												<Pencil className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="sm"
												className="text-destructive"
												onClick={() => setDeleteTarget(q)}
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				) : (
					<p className="text-sm text-muted-foreground">Brak zapytan.</p>
				)}
			</CardContent>

			{editTarget && (
				<QueryEditorDialog
					connectionId={connectionId}
					existing={editTarget === "new" ? null : editTarget}
					onClose={() => setEditTarget(null)}
				/>
			)}

			{verifyTarget && (
				<VerifyDialog
					connectionId={connectionId}
					query={verifyTarget}
					onClose={() => setVerifyTarget(null)}
				/>
			)}

			<ConfirmDialog
				open={!!deleteTarget}
				onOpenChange={(o) => !o && setDeleteTarget(null)}
				title="Usunac zapytanie?"
				description={deleteTarget ? `Zapytanie "${deleteTarget.name}" zostanie usuniete.` : ""}
				variant="destructive"
				confirmLabel="Usun"
				onConfirm={() => {
					if (deleteTarget) deleteMutation.mutate(deleteTarget);
					setDeleteTarget(null);
				}}
			/>
		</Card>
	);
}

// ─── Verify dialog ────────────────────────────────────────────────────────────

/**
 * Eksport raportu verify do Excela: arkusz "Roznice" (format dlugi — wiersz na
 * kazda rozna kolumne) + arkusz "Brak u klienta". `sampleOnly` zmienia tylko
 * sufiks nazwy pliku (raport z full=true ma komplet, bez = sample <=50).
 */
async function exportVerificationToExcel(
	report: SfyVerificationReport,
	opts: { sampleOnly?: boolean } = {},
): Promise<void> {
	const mkCols: ExcelColumn[] = report.matchKeys.map((k, i) => ({
		id: `mk${i}`,
		header: k,
		widthPx: 120,
		isNumeric: false,
	}));

	const diffColumns: ExcelColumn[] = [
		...mkCols,
		{ id: "kolumna", header: "kolumna", widthPx: 130, isNumeric: false },
		{ id: "source", header: "source (Farmaster)", widthPx: 220, isNumeric: false },
		{ id: "destination", header: "destination (klient)", widthPx: 220, isNumeric: false },
	];
	const diffRows: Record<string, unknown>[] = [];
	for (const row of report.sampleDifferences) {
		for (const f of row.differences) {
			const r: Record<string, unknown> = {
				kolumna: f.column,
				source: f.sourceValue ?? "",
				destination: f.destinationValue ?? "",
			};
			row.matchKey.forEach((v, i) => {
				r[`mk${i}`] = v;
			});
			diffRows.push(r);
		}
	}

	const missingRows: Record<string, unknown>[] = report.sampleMissing.map((mk) => {
		const r: Record<string, unknown> = {};
		mk.forEach((v, i) => {
			r[`mk${i}`] = v;
		});
		return r;
	});

	const safe = report.contractName.replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 40);
	const suffix = opts.sampleOnly ? "roznice-sample50" : "roznice";
	await exportToExcel({
		fileName: `sfy-verify-${safe}-${suffix}.xlsx`,
		sheets: [
			{ name: "Roznice", columns: diffColumns, rows: diffRows },
			{ name: "Brak u klienta", columns: mkCols, rows: missingRows },
		],
	});
}

function VerifyDialog({
	connectionId,
	query,
	onClose,
}: {
	connectionId: number;
	query: SfyConnectionQuery;
	onClose: () => void;
}) {
	const [contractId, setContractId] = useState<string>("");
	const [result, setResult] = useState<SfyVerificationReport | null>(null);
	const [explain, setExplain] = useState<SfyExplainReport | null>(null);
	const [error, setError] = useState<string | null>(null);

	const hasAggregateConfig =
		!!query.aggregateSourceSql &&
		!!query.aggregateDestSql &&
		(query.aggregateMatchKeys?.length ?? 0) > 0;
	// Snapshot 'co doslac' dostepny dla trybu agregowanego LUB row (towr i ref.).
	const hasSnapshot = hasAggregateConfig || query.snapshotMode === "row";

	const pinnedQuery = useQuery({
		queryKey: ["sfy", "contracts", connectionId],
		queryFn: () => outboundApi.listSfyContracts(connectionId),
	});

	const allContractsQuery = useQuery({
		queryKey: ["contracts", "all-for-sfy"],
		queryFn: () => contractsApi.list(),
	});

	const contractLookup = useMemo(() => {
		const map = new Map<number, string>();
		for (const c of allContractsQuery.data ?? []) {
			map.set(c.contractId, `${c.name} (${c.year}/${c.quarter}, ${c.producerName})`);
		}
		return map;
	}, [allContractsQuery.data]);

	const runMutation = useMutation({
		mutationFn: () => outboundApi.runSfyVerification(connectionId, query.id, Number(contractId)),
		onSuccess: (r) => {
			setResult(r);
			setError(null);
		},
		onError: (e: unknown) => {
			setResult(null);
			setError(e instanceof Error ? e.message : "Blad verify");
		},
	});

	const explainMutation = useMutation({
		mutationFn: () => outboundApi.explainSfyQuery(connectionId, query.id, Number(contractId)),
		onSuccess: (r) => {
			setExplain(r);
			setError(null);
		},
		onError: (e: unknown) => {
			setExplain(null);
			setError(e instanceof Error ? e.message : "Blad EXPLAIN");
		},
	});

	const aggregateMutation = useMutation({
		mutationFn: () =>
			outboundApi.runSfyAggregateVerification(connectionId, query.id, Number(contractId)),
		onSuccess: (r) => {
			setResult(r);
			setError(null);
		},
		onError: (e: unknown) => {
			setResult(null);
			setError(e instanceof Error ? e.message : "Blad aggregate verify");
		},
	});

	// Eksport: ponowny verify z full=true (pelna lista roznic, bez sample cap 50)
	// -> Excel. Re-run swiadomy (akcja jednorazowa), verify miesci sie w 120s.
	const exportMutation = useMutation({
		mutationFn: async () => {
			const full = await outboundApi.runSfyVerification(
				connectionId,
				query.id,
				Number(contractId),
				true,
			);
			await exportVerificationToExcel(full);
		},
		onError: (e: unknown) => setError(e instanceof Error ? e.message : "Blad eksportu Excel"),
	});

	// Eksport tylko widocznego sample (<=50) — bez re-run, z aktualnego result.
	const exportVisibleMutation = useMutation({
		mutationFn: async () => {
			if (!result) return;
			await exportVerificationToExcel(result, { sampleOnly: true });
		},
		onError: (e: unknown) => setError(e instanceof Error ? e.message : "Blad eksportu Excel"),
	});

	return (
		<Dialog open onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="max-w-4xl">
				<DialogHeader>
					<DialogTitle>Verify: {query.name}</DialogTitle>
					<DialogDescription>
						Porownanie kontraktu wzgledem {query.destinationTable} po stronie klienta. Match keys:{" "}
						<strong>
							{query.columnMappings
								.filter((m) => m.isMatchKey)
								.map((m) => m.sourceAlias)
								.join(", ")}
						</strong>
						.
					</DialogDescription>
				</DialogHeader>
				<DialogBody className="space-y-4">
					<div className="space-y-2">
						<Label>Kontrakt</Label>
						<Select value={contractId} onValueChange={setContractId}>
							<SelectTrigger>
								<SelectValue placeholder="Wybierz przypiety kontrakt" />
							</SelectTrigger>
							<SelectContent>
								{(pinnedQuery.data ?? []).map((p) => (
									<SelectItem key={p.contractId} value={String(p.contractId)}>
										{contractLookup.get(p.contractId) ?? `#${p.contractId}`}
										{!p.sendActive && " — (wstrzymany)"}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						{pinnedQuery.data && pinnedQuery.data.length === 0 && (
							<p className="text-xs text-muted-foreground">
								Brak przypietych kontraktow. Pinuj kontrakt powyzej, zeby uruchomic verify.
							</p>
						)}
					</div>

					{error && <NoticeBanner tone="error" title="Blad" description={error} />}

					{explain && <ExplainPanel explain={explain} onClose={() => setExplain(null)} />}

					{result && <VerifyResultPanel result={result} />}

					{hasSnapshot && contractId && (
						<AggregateSnapshotSection
							connectionId={connectionId}
							queryId={query.id}
							contractId={Number(contractId)}
						/>
					)}
				</DialogBody>
				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Zamknij
					</Button>
					{result && (result.differentRows > 0 || result.missingInDestination > 0) && (
						<>
							<Button
								variant="outline"
								disabled={exportVisibleMutation.isPending}
								onClick={() => exportVisibleMutation.mutate()}
								title="Eksport tylko widocznego sample (<=50 wierszy) bez ponownego verify"
							>
								{exportVisibleMutation.isPending ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<>
										<FileDown className="h-4 w-4 mr-1" />
										Eksport widoczne
									</>
								)}
							</Button>
							<Button
								variant="outline"
								disabled={exportMutation.isPending}
								onClick={() => exportMutation.mutate()}
								title="Pelna lista roznic + brakow (re-run verify bez limitu 50) do pliku .xlsx"
							>
								{exportMutation.isPending ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<>
										<FileDown className="h-4 w-4 mr-1" />
										Eksport pelny
									</>
								)}
							</Button>
						</>
					)}
					{query.aggregateSourceSql &&
						query.aggregateDestSql &&
						(query.aggregateMatchKeys?.length ?? 0) > 0 && (
							<Button
								variant="outline"
								disabled={!contractId || aggregateMutation.isPending}
								onClick={() => aggregateMutation.mutate()}
								title="Agregaty — szybkie porownanie sum i liczebnosci zamiast wiersz-po-wierszu"
							>
								{aggregateMutation.isPending ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									"Tryb agregowany"
								)}
							</Button>
						)}
					<Button
						variant="outline"
						disabled={!contractId || explainMutation.isPending}
						onClick={() => explainMutation.mutate()}
						title="EXPLAIN bez ANALYZE — pokazuje plan zapytania (indeksy, chunk pruning) bez wykonywania"
					>
						{explainMutation.isPending ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							"Pokaz plan (EXPLAIN)"
						)}
					</Button>
					<Button
						disabled={!contractId || runMutation.isPending}
						onClick={() => runMutation.mutate()}
					>
						{runMutation.isPending ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							"Uruchom verify"
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ─── Snapshot agregowanych roznic (co dosłać) ─────────────────────────────────

function formatComputedAt(iso: string | null): string {
	if (!iso) return "nigdy";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return iso;
	return d.toLocaleString("pl-PL");
}

/** Pelny eksport snapshotu dla danego kind — paginacja po 5000 az dostaniemy mniej. */
async function exportAggregateDiffsToExcel(
	connectionId: number,
	queryId: number,
	contractId: number,
	kind: "missing" | "different",
): Promise<void> {
	const PAGE = 5000;
	const all: AggregateDiffRow[] = [];
	let offset = 0;
	// Zabezpieczenie przed nieskonczona petla — twardy limit stron.
	for (let page = 0; page < 1000; page += 1) {
		const batch = await outboundApi.listSfyAggregateDiffs(connectionId, queryId, contractId, {
			kind,
			limit: PAGE,
			offset,
		});
		all.push(...batch);
		if (batch.length < PAGE) break;
		offset += PAGE;
	}

	if (all.length === 0) {
		// Brak danych — i tak generujemy pusty arkusz z naglowkiem informacyjnym.
		await exportToExcel({
			fileName: `sfy-snapshot-${kind}-empty.xlsx`,
			columns: [{ id: "info", header: "Brak wierszy", widthPx: 200, isNumeric: false }],
			rows: [],
		});
		return;
	}

	const matchKeys = Object.keys(all[0].matchKey);
	const valueKeys = Object.keys(all[0].sourceValues);

	const columns: ExcelColumn[] = [];
	for (const k of matchKeys) {
		columns.push({ id: `mk_${k}`, header: k, widthPx: 110, isNumeric: false });
	}
	if (kind === "missing") {
		for (const k of valueKeys) {
			columns.push({ id: `src_${k}`, header: k, widthPx: 110, isNumeric: true });
		}
	} else {
		for (const k of valueKeys) {
			columns.push({ id: `src_${k}`, header: `${k} (nasze)`, widthPx: 120, isNumeric: true });
			columns.push({ id: `dst_${k}`, header: `${k} (klient)`, widthPx: 120, isNumeric: true });
		}
	}

	const rows: Record<string, unknown>[] = all.map((r) => {
		const row: Record<string, unknown> = {};
		for (const k of matchKeys) {
			row[`mk_${k}`] = r.matchKey[k] ?? "";
		}
		for (const k of valueKeys) {
			row[`src_${k}`] = r.sourceValues[k] ?? "";
			if (kind === "different") {
				row[`dst_${k}`] = r.destValues?.[k] ?? "";
			}
		}
		return row;
	});

	await exportToExcel({
		fileName: `sfy-snapshot-${kind}-${all.length}.xlsx`,
		columns,
		rows,
	});
}

function AggregateSnapshotSection({
	connectionId,
	queryId,
	contractId,
}: {
	connectionId: number;
	queryId: number;
	contractId: number;
}) {
	const qc = useQueryClient();
	const [kind, setKind] = useState<"missing" | "different">("missing");
	const [error, setError] = useState<string | null>(null);

	const summaryQuery = useQuery({
		queryKey: ["sfy", "aggregate-summary", connectionId, queryId, contractId],
		queryFn: () => outboundApi.getSfyAggregateDiffsSummary(connectionId, queryId, contractId),
	});

	const diffsQuery = useQuery({
		queryKey: ["sfy", "aggregate-diffs", connectionId, queryId, contractId, kind],
		queryFn: () =>
			outboundApi.listSfyAggregateDiffs(connectionId, queryId, contractId, { kind, limit: 200 }),
	});

	const snapshotMutation = useMutation({
		mutationFn: () => outboundApi.runSfySnapshot(connectionId, queryId, contractId),
		onSuccess: () => {
			setError(null);
			qc.invalidateQueries({
				queryKey: ["sfy", "aggregate-summary", connectionId, queryId, contractId],
			});
			qc.invalidateQueries({
				queryKey: ["sfy", "aggregate-diffs", connectionId, queryId, contractId],
			});
		},
		onError: (e: unknown) =>
			setError(e instanceof Error ? e.message : "Blad przeliczania snapshotu"),
	});

	const exportMutation = useMutation({
		mutationFn: () => exportAggregateDiffsToExcel(connectionId, queryId, contractId, kind),
		onError: (e: unknown) =>
			setError(e instanceof Error ? e.message : "Blad eksportu snapshotu do Excel"),
	});

	const summary = summaryQuery.data;
	const rows = diffsQuery.data ?? [];
	const matchKeys = rows.length > 0 ? Object.keys(rows[0].matchKey) : [];
	const valueKeys = rows.length > 0 ? Object.keys(rows[0].sourceValues) : [];
	const totalForKind = kind === "missing" ? (summary?.missing ?? 0) : (summary?.different ?? 0);

	return (
		<div className="border rounded-md p-3 space-y-3 bg-muted/20">
			<div className="flex items-center justify-between max-md:flex-wrap max-md:gap-1">
				<strong className="text-sm">Snapshot — co dosłać</strong>
				<div className="text-xs text-muted-foreground">
					{summary ? (
						<>
							Braki: <strong>{summary.missing}</strong>, Różnice:{" "}
							<strong>{summary.different}</strong>, ostatnio przeliczono:{" "}
							{formatComputedAt(summary.computedAt)}
						</>
					) : summaryQuery.isLoading ? (
						"ładowanie..."
					) : (
						"brak snapshotu"
					)}
				</div>
			</div>

			{error && <NoticeBanner tone="error" title="Blad" description={error} />}

			<div className="flex flex-wrap items-center gap-2 max-md:[&>button]:h-9">
				<Button
					variant="outline"
					size="sm"
					disabled={snapshotMutation.isPending}
					onClick={() => snapshotMutation.mutate()}
					title="Przelicza pełny snapshot rozbieżności (per apteka) — może potrwać kilka minut"
				>
					{snapshotMutation.isPending ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : (
						"Przelicz snapshot teraz"
					)}
				</Button>
				<div className="flex items-center gap-1">
					<Label className="text-xs">Pokaż:</Label>
					<Select value={kind} onValueChange={(v) => setKind(v as "missing" | "different")}>
						<SelectTrigger className="h-8 w-[140px] max-md:h-9">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="missing">Braki</SelectItem>
							<SelectItem value="different">Różnice</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<Button
					variant="outline"
					size="sm"
					disabled={exportMutation.isPending || totalForKind === 0}
					onClick={() => exportMutation.mutate()}
					title="Pełny eksport snapshotu (paginacja po 5000) do pliku .xlsx"
				>
					{exportMutation.isPending ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : (
						<>
							<FileDown className="h-4 w-4 mr-1" />
							Eksport do Excel
						</>
					)}
				</Button>
			</div>

			<p className="text-xs text-amber-700 dark:text-amber-400">
				Przeliczenie snapshotu jest ciężkie (per apteka) i może potrwać kilka minut.
			</p>

			{diffsQuery.isLoading ? (
				<div className="text-xs text-muted-foreground flex items-center gap-2">
					<Loader2 className="h-3 w-3 animate-spin" /> Ładowanie rozbieżności...
				</div>
			) : rows.length === 0 ? (
				<p className="text-xs text-muted-foreground">
					{kind === "missing" ? "Brak brakujących wierszy." : "Brak różnic."} (Przelicz snapshot,
					jeśli go jeszcze nie wygenerowano.)
				</p>
			) : (
				<div className="text-xs overflow-x-auto">
					<table className="w-full font-mono">
						<thead>
							<tr className="text-muted-foreground border-b">
								{matchKeys.map((k) => (
									<th key={`mk_${k}`} className="text-left px-2 py-1">
										{k}
									</th>
								))}
								{valueKeys.map((k) => (
									<th key={`v_${k}`} className="text-left px-2 py-1">
										{k}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{rows.map((r) => (
								<tr key={r.id} className="border-b last:border-0">
									{matchKeys.map((k) => (
										<td key={`mk_${k}`} className="px-2 py-1 break-all">
											{r.matchKey[k] ?? ""}
										</td>
									))}
									{valueKeys.map((k) => {
										const src = r.sourceValues[k] ?? null;
										if (kind === "missing" || r.destValues == null) {
											return (
												<td key={`v_${k}`} className="px-2 py-1 break-all">
													{src ?? <em>NULL</em>}
												</td>
											);
										}
										const dst = r.destValues[k] ?? null;
										const differs = src !== dst;
										const { opis, show } = invisibleHint(src, dst, differs);
										return (
											<td key={`v_${k}`} className="px-2 py-1 break-all" title={opis ?? undefined}>
												<span>{show(src) ?? <em>NULL</em>}</span>
												<span className="text-muted-foreground"> → </span>
												<span className={differs ? "text-destructive" : ""}>
													{show(dst) ?? <em>NULL</em>}
												</span>
												<InvisibleFlag opis={opis} />
											</td>
										);
									})}
								</tr>
							))}
						</tbody>
					</table>
					{totalForKind > rows.length && (
						<p className="text-xs text-muted-foreground mt-2">
							Pokazano pierwsze {rows.length} z {totalForKind}{" "}
							{kind === "missing" ? "braków" : "różnic"}. Użyj eksportu dla pełnej listy.
						</p>
					)}
				</div>
			)}
		</div>
	);
}

function ExplainPanel({ explain, onClose }: { explain: SfyExplainReport; onClose: () => void }) {
	const [copied, setCopied] = useState(false);
	// Heurystyki diagnostyczne — proste flagi z tekstu planu.
	const planLower = explain.plan.toLowerCase();
	const hasSeqScan = planLower.includes("seq scan");
	const hasHashJoin = planLower.includes("hash join");
	const hints: string[] = [];
	if (hasSeqScan)
		hints.push(
			"Seq Scan w planie — brak indeksu lub planner ignoruje (sprawdz czy datsp ma staly zakres).",
		);
	if (hasHashJoin)
		hints.push(
			"Hash Join — planner czyta caly zakres sprz dla aptek i hashuje. Dla duzego zakresu wolne; nested loop przez idx_sprz_idtowr bywa szybszy.",
		);

	return (
		<div className="border rounded-md p-3 space-y-2 bg-muted/20">
			<div className="flex items-center justify-between max-md:gap-2">
				<strong className="text-sm max-md:min-w-0 max-md:break-words">
					Plan zapytania (EXPLAIN) — {explain.contractName}
				</strong>
				<button
					type="button"
					onClick={onClose}
					className="text-xs text-muted-foreground hover:text-foreground max-md:h-9 max-md:shrink-0 max-md:px-2"
				>
					ukryj
				</button>
			</div>
			<div className="text-xs text-muted-foreground">
				{explain.blozCount} blozow · {explain.unitcodeCount} aptek podstawione
			</div>
			{hints.length > 0 && (
				<ul className="text-xs list-disc pl-4 space-y-0.5 text-amber-700 dark:text-amber-400">
					{hints.map((h) => (
						<li key={h}>{h}</li>
					))}
				</ul>
			)}
			<pre className="text-[11px] font-mono bg-background border rounded p-2 max-h-72 overflow-auto whitespace-pre">
				{explain.plan}
			</pre>
			<div className="flex items-center gap-2">
				<button
					type="button"
					onClick={() => {
						navigator.clipboard.writeText(explain.substitutedSql);
						setCopied(true);
						setTimeout(() => setCopied(false), 1500);
					}}
					className="text-xs underline text-muted-foreground hover:text-foreground max-md:min-h-9 max-md:text-left"
				>
					{copied
						? "skopiowano!"
						: "kopiuj SQL z podstawionymi danymi (do recznego EXPLAIN ANALYZE)"}
				</button>
			</div>
		</div>
	);
}

function VerifyResultPanel({ result }: { result: SfyVerificationReport }) {
	const ok = result.status === "ok";
	const matchKeyHeader = result.matchKeys.join(" / ");
	return (
		<div className="border rounded-md p-3 space-y-3 bg-muted/20">
			<div className="flex items-center gap-2 max-md:flex-wrap">
				{ok ? (
					<CheckCircle2 className="h-5 w-5 text-[var(--success)]" />
				) : (
					<XCircle className="h-5 w-5 text-destructive" />
				)}
				<strong className="max-md:min-w-0 max-md:break-words">{result.contractName}</strong>
				<Badge variant={ok ? "default" : "secondary"}>{result.status}</Badge>
			</div>
			<div className="grid grid-cols-2 gap-2 text-sm">
				<div>
					<span className="text-muted-foreground">Wierszy source:</span>{" "}
					<strong>{result.sourceRowCount}</strong>
				</div>
				<div>
					<span className="text-muted-foreground">Wierszy destination:</span>{" "}
					<strong>{result.destinationRowCount}</strong>
				</div>
				<div>
					<span className="text-muted-foreground">Dopasowanych:</span>{" "}
					<strong>{result.matchedRowCount}</strong>
				</div>
				<div>
					<span className="text-muted-foreground">Brak u klienta:</span>{" "}
					<strong className={result.missingInDestination > 0 ? "text-destructive" : ""}>
						{result.missingInDestination}
					</strong>
				</div>
				<div className="col-span-2">
					<span className="text-muted-foreground">Wierszy z roznicami:</span>{" "}
					<strong className={result.differentRows > 0 ? "text-destructive" : ""}>
						{result.differentRows}
					</strong>
				</div>
			</div>
			{result.notes.length > 0 && (
				<div className="space-y-1">
					<Label className="text-xs">Notatki</Label>
					<ul className="text-xs list-disc pl-4 space-y-0.5">
						{result.notes.map((n) => (
							<li key={n}>{n}</li>
						))}
					</ul>
				</div>
			)}
			{result.sampleMissing.length > 0 && (
				<div className="space-y-1">
					<Label className="text-xs">
						Sample brakujacych ({result.sampleMissing.length} z {result.missingInDestination}) —
						klucze: <span className="font-mono">{matchKeyHeader}</span>
					</Label>
					<div className="font-mono text-xs bg-background border rounded p-2 max-h-40 overflow-y-auto space-y-0.5">
						{result.sampleMissing.map((tuple) => (
							<div key={tuple.join("|")}>{tuple.join(" / ")}</div>
						))}
					</div>
				</div>
			)}
			{result.sampleDifferences.length > 0 && (
				<div className="space-y-1">
					<Label className="text-xs">
						Sample roznic ({result.sampleDifferences.length} z {result.differentRows})
					</Label>
					<div className="text-xs space-y-2 max-h-72 overflow-y-auto">
						{result.sampleDifferences.map((row: SfyRowDifference) => (
							<div key={row.matchKey.join("|")} className="border rounded p-2 bg-background">
								<div className="font-mono text-muted-foreground mb-1">
									{matchKeyHeader}: {row.matchKey.join(" / ")}
								</div>
								<table className="w-full">
									<thead>
										<tr className="text-muted-foreground">
											<th className="text-left">kolumna</th>
											<th className="text-left">source</th>
											<th className="text-left">destination</th>
										</tr>
									</thead>
									<tbody>
										{row.differences.map((d) => (
											<tr key={d.column} className="font-mono">
												<td>{d.column}</td>
												<td className="text-[var(--success)] break-all">
													{d.sourceValue ?? <em>NULL</em>}
												</td>
												<td className="text-destructive break-all">
													{d.destinationValue ?? <em>NULL</em>}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}

// ─── Edytor zapytania (source_sql + column mappings) ──────────────────────────

// Kolumny klienta dla wpisanej destination_table (dopasowanie po pelnej lub golej
// nazwie, posortowane po pozycji). Wspoldzielone przez panel i parenta (generacja).
function clientColumnsFor(
	schema: OutboundSchemaSnapshot | undefined,
	destinationTable: string,
): OutboundSchemaColumn[] {
	const trimmed = destinationTable.trim();
	if (!trimmed || !schema) return [];
	const lower = trimmed.toLowerCase();
	const matched = schema.tables.find((t) => {
		const qual = `${t.tableSchema}.${t.tableName}`.toLowerCase();
		return qual === lower || t.tableName.toLowerCase() === lower;
	});
	if (!matched) return [];
	return schema.columns
		.filter((c) => c.tableSchema === matched.tableSchema && c.tableName === matched.tableName)
		.sort((a, b) => a.ordinalPosition - b.ordinalPosition);
}

// Kolumny techniczne/wewnetrzne klienta — NIE wysylamy ich; ladują na koncu listy
// i sa pomijane przy "zaznacz do generacji".
const TRAILING_COLUMNS = new Set([
	"checked",
	"checkedtime",
	"psdt_akt",
	"global_id",
	"md5",
	"is_imported",
]);
const isTrailingColumn = (name: string) => TRAILING_COLUMNS.has(name.toLowerCase());

function DestinationTableColumns({
	schema,
	loading,
	destinationTable,
	columns,
	checked,
	matchKeyAliases,
	onToggle,
	onSelectGenerable,
	onSetChecked,
}: {
	schema: OutboundSchemaSnapshot | undefined;
	loading: boolean;
	destinationTable: string;
	columns: OutboundSchemaColumn[];
	/** Zaznaczone nazwy kolumn (lowercase). */
	checked: Set<string>;
	/** Aliasy match-key z szablonu (lowercase) — zablokowane, zawsze zaznaczone. */
	matchKeyAliases: Set<string>;
	onToggle: (col: string) => void;
	/** Zaznacza wszystkie kolumny poza koncowymi (technicznymi). */
	onSelectGenerable: () => void;
	/** ZASTEPUJE zaznaczenie podana lista kolumn (lowercase). */
	onSetChecked: (colsLower: string[]) => void;
}) {
	// Szybkie zaznaczanie lista z recznego wpisu — do przenoszenia zapytan ze
	// starego Farmastera, gdzie liste kolumn ma sie juz w SQL-u. Wklejenie
	// "ID,IDFIRM,IDDOKF" ZASTEPUJE zaznaczenie; nieznane nazwy raportujemy
	// zamiast po cichu pomijac (literowka = kolumna cicho niewysylana).
	const [bulkText, setBulkText] = useState("");
	const [bulkInfo, setBulkInfo] = useState<{ matched: number; unknown: string[] } | null>(null);
	const applyBulk = () => {
		const tokens = bulkText
			.split(/[,;\s]+/)
			.map((t) => t.trim())
			.filter(Boolean);
		if (tokens.length === 0) return;
		const byLower = new Map(columns.map((c) => [c.columnName.toLowerCase(), c.columnName]));
		const matched: string[] = [];
		const unknown: string[] = [];
		for (const t of tokens) {
			if (byLower.has(t.toLowerCase())) matched.push(t.toLowerCase());
			else unknown.push(t);
		}
		onSetChecked(matched);
		setBulkInfo({ matched: matched.length, unknown });
	};

	// Alfabetycznie, kolumny techniczne (TRAILING_COLUMNS) na koniec.
	const sortedColumns = [...columns].sort((a, b) => {
		const at = isTrailingColumn(a.columnName);
		const bt = isTrailingColumn(b.columnName);
		if (at !== bt) return at ? 1 : -1;
		return a.columnName.localeCompare(b.columnName);
	});
	const trimmed = destinationTable.trim();
	if (!trimmed) return null;
	if (loading || !schema) {
		return (
			<div className="border rounded-md p-3 text-xs text-muted-foreground flex items-center gap-2">
				<Loader2 className="h-3 w-3 animate-spin" /> Ladowanie schematu klienta...
			</div>
		);
	}
	if (columns.length === 0) {
		return (
			<div className="border rounded-md p-3 text-xs text-muted-foreground">
				Tabela <code className="font-mono">{trimmed}</code> nie istnieje w schemacie klienta —
				zapisana zostanie tak jak wpisana (sprawdz literowke albo dodaj tabele po stronie klienta).
			</div>
		);
	}

	return (
		<div className="border rounded-md text-xs">
			<div className="px-3 py-1.5 border-b bg-muted/30 flex items-center justify-between max-md:flex-wrap max-md:gap-1">
				<span className="font-medium max-md:min-w-0 max-md:break-all">
					Kolumny <code className="font-mono">{trimmed}</code> ({columns.length})
				</span>
				<button
					type="button"
					onClick={onSelectGenerable}
					className="text-muted-foreground hover:text-foreground hover:underline max-md:min-h-9"
					title="Zaznacz wszystkie kolumny poza technicznymi (checked, checkedTime, PSDT_AKT, GLOBAL_ID, md5, IS_IMPORTED)"
				>
					zaznacz do generacji
				</button>
			</div>
			<div className="flex items-center gap-2 border-b px-3 py-1.5 max-md:flex-wrap">
				<input
					value={bulkText}
					onChange={(e) => setBulkText(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							applyBulk();
						}
					}}
					placeholder="wklej liste kolumn po przecinku, np. ID,IDFIRM,IDDOKF — Enter zaznacza"
					className="h-7 flex-1 rounded-md border border-input bg-background px-2 font-mono text-[11px] max-md:h-9 max-md:min-w-0 max-md:basis-full"
					title="ZASTEPUJE obecne zaznaczenie kolumnami z listy (match keys zawsze zostaja). Separatory: przecinek, srednik, spacja."
				/>
				<button
					type="button"
					onClick={applyBulk}
					className="text-muted-foreground hover:text-foreground hover:underline max-md:min-h-9"
				>
					zaznacz z listy
				</button>
			</div>
			{bulkInfo && (
				<div className="border-b px-3 py-1 text-[11px] max-md:wrap-anywhere">
					<span className="text-muted-foreground">zaznaczono {bulkInfo.matched}</span>
					{bulkInfo.unknown.length > 0 && (
						<span className="text-status-warning-text">
							{" "}
							· nieznane w tabeli klienta: {bulkInfo.unknown.join(", ")}
						</span>
					)}
				</div>
			)}
			<div className="p-2 grid grid-cols-1 md:grid-cols-3 gap-x-3 gap-y-0.5 max-h-40 overflow-y-auto max-md:max-h-60">
				{sortedColumns.map((c) => {
					const isKey = matchKeyAliases.has(c.columnName.toLowerCase());
					const isChecked = isKey || checked.has(c.columnName.toLowerCase());
					return (
						<label
							key={c.columnName}
							className="flex items-center gap-1 truncate cursor-pointer max-md:min-h-9"
							title={`${c.dataType}${c.isNullable ? " NULL" : ""}${isKey ? " — match key" : ""}`}
						>
							<input
								type="checkbox"
								checked={isChecked}
								disabled={isKey}
								onChange={() => onToggle(c.columnName)}
								className="rounded border-input"
							/>
							<span className="font-mono truncate">{c.columnName}</span>
							<span className="text-muted-foreground">({c.dataType})</span>
						</label>
					);
				})}
			</div>
		</div>
	);
}

const PLACEHOLDER_HINTS = [
	":BLOZ07 — blozy z kontraktu jako IN-list",
	":UNITCODES — apteki kontraktu jako IN-list",
	":CONTRACT_DATE_FROM — poczatek kwartalu kontraktu (auto z year+quarter)",
	":CONTRACT_DATE_TO — koniec kwartalu kontraktu (auto z year+quarter)",
	":MAXDATE — data cutoff (default '1900-01-01' dla verify)",
	":CONTRACT_ID — bigint id kontraktu",
	":STOCK_FROM — dzis - 2 lata (okno stanu kzak, boundowanie hipertabeli)",
];

/** Znacznik rozjazdu zapisanego SQL-a z aktualnym szablonem.
 *
 *  Zapisane `source_sql` i `aggregate_*` to KOPIE w bazie — zmiana szablonu ich nie
 *  dotyka, a regeneruje sie je osobnymi przyciskami, wiec potrafia rozjechac sie tez
 *  miedzy soba. Kosztowalo to juz trzy dochodzenia (brak nawiasow wokol tabel
 *  2022BauschLomb*, agregat po t.bloz07 przy zrodle po k.bloz07). Znacznik ma uciac
 *  temat na wejsciu — dlatego jest informacyjny, a nie alarmowy: czesc zapytan ma
 *  SQL dopieszczony recznie i tam rozjazd jest stanem normalnym.
 */
function TemplateDriftBadge({ query }: { query: SfyConnectionQuery }) {
	const drift = templateDrift(query);
	if (!drift || (!drift.sourceSql && !drift.aggregate)) return null;
	const parts = [drift.sourceSql && "source_sql", drift.aggregate && "agregat"].filter(
		Boolean,
	) as string[];
	return (
		<span
			className="ml-2 rounded border border-status-warning-border bg-status-warning-bg px-1.5 py-0.5 text-[10px] text-status-warning-text"
			title={`Zapisany ${parts.join(" i ")} rozni sie od tego, co wygenerowalby dzis szablon. Jesli SQL nie byl dopieszczany recznie — przegeneruj i zapisz, a potem przelicz snapshot. Rozjazd miedzy zrodlem a agregatem daje aligned=false i pusty podglad kubelka.`}
		>
			≠ szablon ({parts.join(", ")})
		</span>
	);
}

function QueryEditorDialog({
	connectionId,
	existing,
	onClose,
}: {
	connectionId: number;
	existing: SfyConnectionQuery | null;
	onClose: () => void;
}) {
	const qc = useQueryClient();
	const isEdit = existing != null;

	const schemaQuery = useQuery({
		queryKey: ["outbound", "schema", connectionId],
		queryFn: () => outboundApi.getConnectionSchema(connectionId),
		staleTime: 5 * 60 * 1000,
	});

	const [name, setName] = useState(existing?.name ?? "");
	const [ourTable, setOurTable] = useState(existing?.ourTable ?? "");
	const [destinationTable, setDestinationTable] = useState(existing?.destinationTable ?? "");
	const [sourceSql, setSourceSql] = useState(existing?.sourceSql ?? "");
	const [sqlFarmasterOld, setSqlFarmasterOld] = useState(existing?.sqlFarmasterOld ?? "");
	const [mappings, setMappings] = useState<SfyColumnMapping[]>(existing?.columnMappings ?? []);
	const [active, setActive] = useState(existing?.active ?? true);
	const [pushActive, setPushActive] = useState(existing?.pushActive ?? false);
	const [autoApply, setAutoApply] = useState(existing?.autoApply ?? false);
	const [autoMaxUpserts, setAutoMaxUpserts] = useState<number>(existing?.autoMaxUpserts ?? 20000);
	const [autoMaxPerFirm, setAutoMaxPerFirm] = useState<number>(existing?.autoMaxPerFirm ?? 100);
	const [notes, setNotes] = useState(existing?.notes ?? "");
	const [aggregateSourceSql, setAggregateSourceSql] = useState(existing?.aggregateSourceSql ?? "");
	const [aggregateDestSql, setAggregateDestSql] = useState(existing?.aggregateDestSql ?? "");
	const [aggregateMatchKeysRaw, setAggregateMatchKeysRaw] = useState(
		(existing?.aggregateMatchKeys ?? []).join(", "),
	);
	const [snapshotMode, setSnapshotMode] = useState<SfySnapshotMode>(
		existing?.snapshotMode ?? "off",
	);
	const [error, setError] = useState<string | null>(null);
	const [analyzeError, setAnalyzeError] = useState<string | null>(null);
	const [detectedAliases, setDetectedAliases] = useState<string[]>(
		existing?.columnMappings.map((m) => m.sourceAlias) ?? [],
	);
	const [detectedPlaceholders, setDetectedPlaceholders] = useState<string[]>([]);
	// Potwierdzenie nadpisania przy generowaniu (ConfirmDialog — NIE window.confirm,
	// ktory Chrome suppressuje przy nieaktywnej karcie i cicho zwraca false).
	const [confirmGen, setConfirmGen] = useState<"default" | "aggregate" | null>(null);

	// Auto-generacja: szablon our_table + kolumny klienta z checkboxami.
	const template = getTemplate(ourTable);
	const clientCols = useMemo(
		() => clientColumnsFor(schemaQuery.data, destinationTable),
		[schemaQuery.data, destinationTable],
	);
	const matchKeyAliases = useMemo(
		() =>
			new Set(
				(template?.columns ?? []).filter((c) => c.matchKey).map((c) => c.alias.toLowerCase()),
			),
		[template],
	);
	const [checkedCols, setCheckedCols] = useState<Set<string>>(new Set());

	const doGenerateDefault = () => {
		if (!template) return;
		const checkedClientColumns = clientCols
			.map((c) => c.columnName)
			.filter((n) => matchKeyAliases.has(n.toLowerCase()) || checkedCols.has(n.toLowerCase()));
		const gen = buildSourceSqlAndMappings(
			template,
			checkedClientColumns,
			snapshotMode === "aggregate",
		);
		setSourceSql(gen.sourceSql);
		setMappings(gen.mappings);
		setDetectedAliases(gen.mappings.map((m) => m.sourceAlias));
	};
	const doGenerateAggregate = () => {
		if (!template) return;
		const prefix = deriveClientPrefix(
			destinationTable,
			(schemaQuery.data?.tables ?? []).map((t) => `${t.tableSchema}.${t.tableName}`),
		);
		const agg = buildAggregate(template, prefix);
		if (!agg) return;
		setSnapshotMode(agg.snapshotMode);
		setAggregateMatchKeysRaw(agg.matchKeys.join(", "));
		setAggregateSourceSql(agg.aggregateSourceSql);
		setAggregateDestSql(agg.aggregateDestSql);
	};

	// Inicjalizacja zaznaczen: przy EDYCJI istniejacego zapytania (ta sama our_table)
	// z zapisanych column_mappings (odbicie SQL); dla nowego / po zmianie our_table z
	// domyslnych szablonu. Ref pilnuje, by nie klobrowac togglow po asynchronicznym
	// zaladowaniu schematu (ten sam our_table -> nie ruszamy).
	const lastInitTableRef = useRef<string | null>(null);
	useEffect(() => {
		if (!template || clientCols.length === 0) return;
		if (lastInitTableRef.current === ourTable) return;
		const firstInit = lastInitTableRef.current === null;
		lastInitTableRef.current = ourTable;
		if (firstInit && existing?.ourTable === ourTable && existing.columnMappings.length > 0) {
			setCheckedCols(new Set(existing.columnMappings.map((m) => m.destColumn.toLowerCase())));
		} else {
			const def = defaultCheckedColumns(
				template,
				clientCols.map((c) => c.columnName),
			);
			setCheckedCols(new Set(def.map((n) => n.toLowerCase())));
		}
	}, [ourTable, template, clientCols, existing]);
	const toggleCol = (col: string) =>
		setCheckedCols((prev) => {
			const next = new Set(prev);
			const key = col.toLowerCase();
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});

	const analyzeMutation = useMutation({
		mutationFn: () => outboundApi.analyzeSfySql(sourceSql),
		onSuccess: (a) => {
			setAnalyzeError(null);
			setDetectedAliases(a.aliases);
			setDetectedPlaceholders(a.placeholders);
			// merge: zachowaj istniejace mappingi gdzie alias pasuje (case-insensitive),
			// dodaj nowe dla aliasow ktorych jeszcze nie ma.
			setMappings((current) => {
				const byAlias = new Map(current.map((m) => [m.sourceAlias.toLowerCase(), m]));
				return a.aliases.map(
					(alias) =>
						byAlias.get(alias.toLowerCase()) ?? {
							sourceAlias: alias,
							destColumn: alias,
							isMatchKey: false,
							anonimize: false,
						},
				);
			});
		},
		onError: (e: unknown) =>
			setAnalyzeError(e instanceof Error ? e.message : "Nieznany blad analizy SQL"),
	});

	function updateMapping(idx: number, patch: Partial<SfyColumnMapping>) {
		setMappings((ms) =>
			ms.map((m, i) => {
				if (i !== idx) return m;
				const next = { ...m, ...patch };
				if (!next.isMatchKey) next.anonimize = false; // anonimize tylko dla match keys
				return next;
			}),
		);
	}

	const saveMutation = useMutation({
		mutationFn: () => {
			const payload: UpsertSfyQueryRequest = {
				name: name.trim(),
				ourTable: ourTable.trim(),
				destinationTable: destinationTable.trim(),
				sourceSql,
				sqlFarmasterOld: (sqlFarmasterOld ?? "").trim() || null,
				columnMappings: mappings,
				active,
				pushActive: active && pushActive,
				autoApply: active && pushActive && autoApply,
				autoMaxUpserts,
				autoMaxPerFirm,
				notes: (notes ?? "").trim() || null,
				aggregateSourceSql: aggregateSourceSql.trim() || null,
				aggregateDestSql: aggregateDestSql.trim() || null,
				aggregateMatchKeys: aggregateMatchKeysRaw
					.split(",")
					.map((k) => k.trim())
					.filter(Boolean),
				snapshotMode,
			};
			return existing
				? outboundApi.updateSfyQuery(connectionId, existing.id, payload)
				: outboundApi.createSfyQuery(connectionId, payload);
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["sfy", "queries", connectionId] });
			onClose();
		},
		onError: (e: unknown) => setError(e instanceof Error ? e.message : "Blad zapisu"),
	});

	const hasMatchKey = mappings.some((m) => m.isMatchKey);

	return (
		<Dialog open onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="max-w-5xl">
				<DialogHeader>
					<DialogTitle>{isEdit ? `Edycja: ${existing?.name}` : "Nowe zapytanie"}</DialogTitle>
					<DialogDescription>
						Pelen SQL po naszej stronie z placeholderami + mapowanie aliasow na kolumny po stronie
						klienta. Walidacja przez sqlparser-rs po zapisie.
					</DialogDescription>
				</DialogHeader>
				<DialogBody className="space-y-4">
					{error && (
						<div className="sticky top-0 z-10 -mt-1 pt-1 pb-1 bg-background">
							<NoticeBanner
								tone="error"
								title="Blad zapisu"
								description={error}
								onDismiss={() => setError(null)}
							/>
						</div>
					)}
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
						<div className="space-y-1 min-w-0">
							<Label>Nazwa</Label>
							<Input value={name} onChange={(e) => setName(e.target.value)} />
						</div>
						<div className="space-y-1 min-w-0">
							<Label>
								our_table <span className="text-xs text-muted-foreground">(label)</span>
							</Label>
							<Select value={ourTable} onValueChange={setOurTable}>
								<SelectTrigger className="w-full min-w-0 [&>span]:truncate">
									<SelectValue placeholder="wybierz tabele" />
								</SelectTrigger>
								<SelectContent>
									{OUR_TABLE_TEMPLATES.map((t) => (
										<SelectItem key={t.value} value={t.value}>
											{t.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-1 min-w-0">
							<Label>destination_table</Label>
							<Input
								value={destinationTable}
								onChange={(e) => setDestinationTable(e.target.value)}
								placeholder="dbo.HerbapolpKZAK"
								list="sfy-destination-tables"
							/>
							<datalist id="sfy-destination-tables">
								{(schemaQuery.data?.tables ?? []).map((t) => (
									<option
										key={`${t.tableSchema}.${t.tableName}`}
										value={`${t.tableSchema}.${t.tableName}`}
									/>
								))}
							</datalist>
						</div>
					</div>

					<div className="flex flex-wrap items-center gap-4">
						<label className="flex items-center gap-2 cursor-pointer text-sm">
							<input
								type="checkbox"
								checked={active}
								onChange={(e) => setActive(e.target.checked)}
								className="rounded border-input"
							/>
							Aktywne <span className="text-xs text-muted-foreground">(kontrola / przeglad)</span>
						</label>
						<label
							className={cn(
								"flex items-center gap-2 text-sm",
								active ? "cursor-pointer" : "cursor-not-allowed opacity-50",
							)}
							title="Wlacz DOPIERO po dokladnym przetestowaniu — insert/update leci do klienta. Wymaga tez wlaczonej wysylki na kontrakcie. Domyslnie wylaczone."
						>
							<input
								type="checkbox"
								checked={active && pushActive}
								disabled={!active}
								onChange={(e) => setPushActive(e.target.checked)}
								className="rounded border-input"
							/>
							<span className="text-status-error-text font-medium">Wysylaj do klienta</span>
							<span className="text-xs text-muted-foreground">(insert/update — apply)</span>
						</label>
					</div>

					{/* Automat — nadzbior wysylki recznej. Wlaczaj dopiero po kilku udanych
					    cyklach recznych. Cron pomija rozjazdy tylko na dgakt i wstrzymuje
					    sie powyzej limitu (nie gasi flagi — wraca sam, gdy wolumen spadnie). */}
					<div className="flex flex-wrap items-center gap-4">
						<label
							className={cn(
								"flex items-center gap-2 text-sm",
								active && pushActive ? "cursor-pointer" : "cursor-not-allowed opacity-50",
							)}
							title="Nocny cron wysyla to zapytanie BEZ nadzoru. Wymaga wlaczonego 'Wyslij do klienta'. Wysylka tylko gdy sa istotne rozjazdy (sam dgakt pomijany)."
						>
							<input
								type="checkbox"
								checked={active && pushActive && autoApply}
								disabled={!(active && pushActive)}
								onChange={(e) => setAutoApply(e.target.checked)}
								className="rounded border-input"
							/>
							<span className="font-medium">Automat (nocny cron)</span>
							<span className="text-xs text-muted-foreground">(wysylka bez nadzoru)</span>
						</label>
						<label
							className={cn("flex items-center gap-2 text-sm", autoApply ? "" : "opacity-50")}
							title="GLOWNY bezpiecznik: gdy KTORAKOLWIEK apteka ma wiecej wpisow niz tyle, automat wstrzymuje caly przebieg i wypisuje w audycie, ktore to apteki. Limit calkowity rosnie z liczba aptek i alarmuje na normalnej nocy — ten mierzy anomalie tam, gdzie ona siedzi."
						>
							<span className="text-xs text-muted-foreground">Limit wpisow na aptekę</span>
							<input
								type="number"
								min={1}
								value={autoMaxPerFirm}
								disabled={!autoApply}
								onChange={(e) => setAutoMaxPerFirm(Number(e.target.value) || 1)}
								className="w-24 rounded-md border border-input bg-background px-2 py-1 text-sm max-md:h-9"
							/>
						</label>
						<label
							className="flex items-center gap-2"
							title="Ostatnia deska ratunku, nie pokretlo przepustowosci: prog, powyzej ktorego absurdalna jest sama SUMA, niezaleznie od rozlozenia na apteki. Codzienna prace gejtuje limit na aptekę obok."
						>
							<span className="text-xs text-muted-foreground">Limit calkowity</span>
							<input
								type="number"
								min={1}
								value={autoMaxUpserts}
								disabled={!autoApply}
								onChange={(e) => setAutoMaxUpserts(Number(e.target.value) || 1)}
								className="w-24 rounded-md border border-input bg-background px-2 py-1 text-sm max-md:h-9"
							/>
						</label>
					</div>

					<div className="space-y-2 pt-2 border-t">
						<Label>
							Snapshot na dashboardzie przegladu — wybierz najpierw, ustala co uzupelnic
						</Label>
						<select
							className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
							value={snapshotMode}
							onChange={(e) => setSnapshotMode(e.target.value as SfySnapshotMode)}
						>
							<option value="off">off — poza nocnym snapshotem (niemonitorowane)</option>
							<option value="aggregate">aggregate — sumy/liczby (duze tabele: sprz, kzak)</option>
							<option value="row">row — porownanie wiersz-po-wierszu (towr i ref.)</option>
							<option value="completeness">
								completeness — domkniecie referencyjne (dokf / kzak)
							</option>
						</select>
						{snapshotMode === "completeness" && (
							<p className="rounded-md border border-status-info-border bg-status-info-bg px-2 py-1.5 text-status-info-text text-xs">
								{ourTable === "dokf"
									? "Referujemy dokf przez KZAK, KORZ, SPRZ klienta (po iddokf). Braki INSERT + roznice UPDATE. Klucz (idfirm, id), okno 2 lata."
									: ourTable === "kzak"
										? "Referujemy kzak przez SPRZ, KORZ klienta (po idkzak). TYLKO INSERT brakow (anti-join klienta) — stan/update robi osobny agregat 'kzak stan'. Okno 2 lata."
										: "Completeness wspiera tylko dokf i kzak — wybierz jedno z nich w 'our_table'."}
							</p>
						)}
						<p className="text-muted-foreground text-xs">
							{snapshotMode === "row"
								? "row: wymaga zmapowania WSZYSTKICH kolumn projekcji (w tym wyliczanych) — inaczej byłyby cicho pomijane."
								: snapshotMode === "aggregate"
									? "aggregate: wypelnij SQL-e agregatu ponizej + klucze; naprawa rozwija rozjechane kubelki do detalu."
									: snapshotMode === "completeness"
										? "completeness: liczy sie TYLKO mapowanie kolumn (id, idfirm + kolumny) i destination_table. source_sql i agregat sa NIEUZYWANE (zapytanie buduje sie samo z referencji klienta)."
										: "off: zapytanie poza nocnym snapshotem."}
						</p>
					</div>

					<DestinationTableColumns
						schema={schemaQuery.data}
						loading={schemaQuery.isLoading}
						destinationTable={destinationTable}
						columns={clientCols}
						checked={checkedCols}
						matchKeyAliases={matchKeyAliases}
						onToggle={toggleCol}
						onSelectGenerable={() =>
							setCheckedCols(
								new Set(
									clientCols
										.filter((c) => !isTrailingColumn(c.columnName))
										.map((c) => c.columnName.toLowerCase()),
								),
							)
						}
						onSetChecked={(cols) => setCheckedCols(new Set(cols))}
					/>

					<div className="space-y-2">
						<div className="flex items-center justify-between max-md:flex-wrap max-md:gap-2">
							<Label>
								source_sql{" "}
								<span className="text-xs text-muted-foreground">
									{snapshotMode === "completeness"
										? "(nieuzywany w completeness — liczy sie tylko mapowanie ponizej)"
										: "(PG, prefix prima.)"}
								</span>
							</Label>
							<div className="flex gap-2 max-md:flex-wrap max-md:[&>button]:h-9">
								<Button
									variant="ghost"
									size="sm"
									disabled={!template || clientCols.length === 0}
									onClick={() => {
										if (sourceSql.trim() || mappings.length > 0) setConfirmGen("default");
										else doGenerateDefault();
									}}
								>
									{snapshotMode === "completeness"
										? "Aktualizuj mapowanie"
										: "Wygeneruj domyslny SQL"}
								</Button>
								{snapshotMode !== "completeness" && template?.aggregate && (
									<Button
										variant="ghost"
										size="sm"
										onClick={() => {
											if (aggregateSourceSql.trim() || aggregateDestSql.trim())
												setConfirmGen("aggregate");
											else doGenerateAggregate();
										}}
									>
										Wygeneruj agregacje
									</Button>
								)}
								{snapshotMode !== "completeness" && (
									<Button
										variant="outline"
										size="sm"
										onClick={() => analyzeMutation.mutate()}
										disabled={!sourceSql.trim() || analyzeMutation.isPending}
									>
										{analyzeMutation.isPending ? (
											<Loader2 className="h-3 w-3 animate-spin" />
										) : (
											"Analizuj SQL"
										)}
									</Button>
								)}
							</div>
						</div>
						{snapshotMode !== "completeness" && (
							<textarea
								className={`${TEXTAREA_CLASS} font-mono text-xs`}
								rows={10}
								value={sourceSql}
								onChange={(e) => setSourceSql(e.target.value)}
								placeholder={`SELECT k.id, k.idfirm, t.bloz07 as bloz07, k.ilows
FROM prima.kzak k, prima.dokf d, prima.towr t
WHERE k.idfirm = d.idfirm AND k.iddokf = d.id
  AND d.datas >= '2023-10-01'
  AND k.idfirm = t.idfirm AND t.id = k.idtowr
  AND k.idfirm IN (:UNITCODES)
  AND t.bloz07 IN (:BLOZ07)`}
							/>
						)}
						<details className="text-xs text-muted-foreground">
							<summary className="cursor-pointer hover:text-foreground">
								Dostepne placeholdery
							</summary>
							<ul className="list-disc pl-5 mt-1 space-y-0.5">
								{PLACEHOLDER_HINTS.map((h) => (
									<li key={h}>
										<code>{h.split(" — ")[0]}</code> — {h.split(" — ")[1]}
									</li>
								))}
							</ul>
						</details>
						{analyzeError && (
							<NoticeBanner tone="error" title="Analiza SQL" description={analyzeError} />
						)}
						{detectedAliases.length > 0 && (
							<p className="text-xs text-muted-foreground max-md:wrap-anywhere">
								Wykryte aliasy: <span className="font-mono">{detectedAliases.join(", ")}</span>
								{detectedPlaceholders.length > 0 && (
									<>
										{" "}
										· placeholdery:{" "}
										<span className="font-mono">{detectedPlaceholders.join(", ")}</span>
									</>
								)}
							</p>
						)}
					</div>

					{snapshotMode !== "completeness" && (
						<div className="space-y-2">
							<Label>
								sql_farmaster_old{" "}
								<span className="text-xs text-muted-foreground">
									(archiwalny SQL ze starego systemu — tylko do porownan, nie wykonywany)
								</span>
							</Label>
							<textarea
								className={`${TEXTAREA_CLASS} font-mono text-xs`}
								rows={6}
								value={sqlFarmasterOld}
								onChange={(e) => setSqlFarmasterOld(e.target.value)}
								placeholder="Wklej tu stary SQL z Farmaster, zeby w razie rozjazdow porownac z nowym source_sql."
							/>
						</div>
					)}

					{snapshotMode === "aggregate" && (
						<div className="space-y-2 pt-2 border-t">
							<Label className="text-muted-foreground text-xs">
								Tryb agregowany — SQL sum/liczb (nasz PG + T-SQL klienta) + klucze agregacji
							</Label>
							<Label>Klucze agregacji (oddzielone przecinkiem)</Label>
							<Input
								placeholder="idfirm, bloz07, miesiac"
								value={aggregateMatchKeysRaw}
								onChange={(e) => setAggregateMatchKeysRaw(e.target.value)}
							/>
							<p className="text-muted-foreground text-xs">
								Klucz produktu = bloz07 (globalny). NIE idtowr — towr ma klucz (idfirm, id), wiec
								idtowr powtarza sie miedzy aptekami jako rozne produkty. Joinuj towr po OBU stronach
								(klient ma swoja tabele TOWR) i grupuj po bloz07.
							</p>
							<Label>SQL agregowany (PostgreSQL)</Label>
							<textarea
								className={`${TEXTAREA_CLASS} font-mono text-xs`}
								rows={6}
								placeholder="SELECT s.idfirm, t.bloz07, TO_CHAR(s.datsp, 'YYYY-MM') AS miesiac, SUM(s.ilosp) AS suma_ilosp, COUNT(*) AS liczba_wierszy FROM prima.sprz s JOIN prima.towr t ON t.idfirm = s.idfirm AND t.id = s.idtowr WHERE s.datsp >= :CONTRACT_DATE_FROM AND s.datsp <= :CONTRACT_DATE_TO AND s.idfirm IN (:UNITCODES) AND t.bloz07 IN (:BLOZ07) GROUP BY s.idfirm, t.bloz07, TO_CHAR(s.datsp, 'YYYY-MM')"
								value={aggregateSourceSql ?? ""}
								onChange={(e) => setAggregateSourceSql(e.target.value)}
							/>
							<Label>SQL agregowany (SQL Server / T-SQL)</Label>
							<textarea
								className={`${TEXTAREA_CLASS} font-mono text-xs`}
								rows={6}
								placeholder="SELECT s.IDFIRM AS idfirm, t.BLOZ07 AS bloz07, CONVERT(CHAR(7), s.DATSP, 120) AS miesiac, SUM(s.ILOSP) AS suma_ilosp, COUNT(*) AS liczba_wierszy FROM dbo.ClientSPRZ s JOIN dbo.ClientTOWR t ON t.IDFIRM = s.IDFIRM AND t.ID = s.IDTOWR WHERE s.DATSP >= :CONTRACT_DATE_FROM AND s.DATSP <= :CONTRACT_DATE_TO AND s.IDFIRM IN (:UNITCODES) AND t.BLOZ07 IN (:BLOZ07) GROUP BY s.IDFIRM, t.BLOZ07, CONVERT(CHAR(7), s.DATSP, 120)"
								value={aggregateDestSql ?? ""}
								onChange={(e) => setAggregateDestSql(e.target.value)}
							/>
						</div>
					)}

					<div className="space-y-2">
						<Label>Mapowanie kolumn (alias → dest column klienta) najczesciej id idfirm</Label>
						{mappings.length === 0 ? (
							<p className="text-xs text-muted-foreground">
								Kliknij "Analizuj SQL" zeby wykryc aliasy z projekcji i wygenerowac mapowanie.
							</p>
						) : (
							<div className="border rounded-md overflow-hidden max-md:overflow-x-auto">
								<table className="w-full text-xs">
									<thead className="bg-muted/50">
										<tr>
											<th className="text-left p-2">source alias</th>
											<th className="text-left p-2">dest column</th>
											<th className="text-center p-2">match key</th>
											<th className="text-center p-2">anonimize</th>
										</tr>
									</thead>
									<tbody>
										{mappings.map((m, idx) => (
											<tr key={m.sourceAlias} className="border-t">
												<td className="p-2 font-mono">{m.sourceAlias}</td>
												<td className="p-1 max-md:min-w-[9rem]">
													<Input
														className="h-7 text-xs max-md:h-9"
														value={m.destColumn}
														onChange={(e) => updateMapping(idx, { destColumn: e.target.value })}
														placeholder={m.sourceAlias}
													/>
												</td>
												<td className="p-2 text-center">
													<input
														type="checkbox"
														checked={m.isMatchKey}
														onChange={(e) => updateMapping(idx, { isMatchKey: e.target.checked })}
														className="rounded border-input"
													/>
												</td>
												<td className="p-2 text-center">
													<input
														type="checkbox"
														checked={m.anonimize}
														disabled={!m.isMatchKey}
														onChange={(e) => updateMapping(idx, { anonimize: e.target.checked })}
														className="rounded border-input"
													/>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
						{mappings.length > 0 && !hasMatchKey && (
							<p className="text-xs text-destructive">
								Wymagany co najmniej 1 wiersz z zaznaczonym "match key".
							</p>
						)}
					</div>

					<div className="space-y-1">
						<Label>Notatki (opcjonalne)</Label>
						<textarea
							className={TEXTAREA_CLASS}
							rows={2}
							value={notes ?? ""}
							onChange={(e) => setNotes(e.target.value)}
						/>
					</div>
				</DialogBody>
				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Anuluj
					</Button>
					<Button
						onClick={() => saveMutation.mutate()}
						disabled={saveMutation.isPending || !hasMatchKey || mappings.length === 0}
					>
						{saveMutation.isPending ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : isEdit ? (
							"Zapisz"
						) : (
							"Utworz"
						)}
					</Button>
				</DialogFooter>
				<ConfirmDialog
					open={confirmGen !== null}
					onOpenChange={(o) => !o && setConfirmGen(null)}
					variant="warning"
					title={
						confirmGen === "aggregate"
							? "Nadpisac agregat?"
							: snapshotMode === "completeness"
								? "Nadpisac mapowanie?"
								: "Nadpisac source_sql i mapowanie?"
					}
					description={`Zostanie nadpisane domyslnym szablonem dla ${ourTable}.`}
					confirmLabel="Nadpisz"
					onConfirm={() => {
						if (confirmGen === "aggregate") doGenerateAggregate();
						else if (confirmGen === "default") doGenerateDefault();
						setConfirmGen(null);
					}}
				/>
			</DialogContent>
		</Dialog>
	);
}

// ─── Sprawdź aptekę: kompletność jednej apteki we wszystkich zapytaniach ───────
